import { Fragment, useEffect, useState } from 'react';
import { api, liste, zeitpunkt, datum, darf, type Anmelder } from '../api.ts';
import { Kopf, Karte, Leer } from '../teile/Geruest.tsx';
import { Abschnittsband, kennung } from '../teile/Abschnittsband.tsx';
import {
  Dialog, Fehlerkasten, Haken, Text, ausFehler, type Fehlerlage,
} from '../teile/Formular.tsx';
import { MeineSicherheit } from './Faktor.tsx';
import Abwurf from '../teile/Abwurf.tsx';
import { Passwortregeln, usePasswortvorgabe } from '../teile/Passwortregeln.tsx';
import { zufallswert } from '../teile/zufallswert.ts';
import { Ortssperre } from '../teile/Ortssperre.tsx';
import { Firmen } from './Stammdaten.tsx';
import { Ne4Teams } from './Ne4.tsx';
import { useFrisch } from '../teile/Frisch.tsx';
import { Sicherung } from '../teile/Sicherung.tsx';
import { Update } from '../teile/Update.tsx';
import { fragen } from '../teile/rueckfrage.tsx';

// ============================================================================
// Benutzer und Rechte
//
// Marvin, 23.08.2026:
//   "Wir programmieren eine rexhteverwaltubg wo ich Gruppen und Benutzern
//    Zugriff erteilen kann zb auf Module und Funktionen."
//
// Drei Reiter, weil es drei Fragen sind:
//   BENUTZER  wer ist da, in welchen Gruppen, was gilt fuer ihn am Ende
//   GRUPPEN   welche Buendel gibt es, was traegt jedes
//   RECHTE    welche Module und Funktionen gibt es ueberhaupt
//
// Der dritte Reiter ist absichtlich nur zum Lesen: welche Funktionen es gibt,
// entscheidet der Bau, nicht die Verwaltung. Wer dort eine Zeile anlegen
// koennte, haette ein Recht ohne Programmcode dahinter.
// ============================================================================

type Reiter = 'benutzer' | 'gruppen' | 'rechte' | 'firmen' | 'sicherheit';

export function Benutzer({
  ich, uebernommen,
}: {
  ich: Anmelder;
  uebernommen?: () => void;
}) {
  /* .577: WER NUR FIRMEN UND TEAMS PFLEGT (NE4-Dispo), SIEHT NUR DIESEN REITER. */
  const verwaltet = darf(ich, 'benutzer.verwalten');
  const [reiter, setReiter] = useState<Reiter>(verwaltet ? 'benutzer' : 'firmen');
  const [benutzer, setBenutzer] = useState<any[]>([]);
  const [gruppen, setGruppen] = useState<any[]>([]);
  const [module, setModule] = useState<any[]>([]);
  const [fehler, setFehler] = useState<string | null>(null);
  const [gewaehlt, setGewaehlt] = useState<string | null>(null);

  /* HIER STAND EINE ENGE SICHT FÜR DIE BAULEITUNG, und sie ist am 31.08.2026
     wieder gefallen: „die bauleiter legen keine zugänge an, das macht alles
     der innendienst. wir haben die oberhand." Diesen Bildschirm sieht nur,
     wer `benutzer.verwalten` hat, und das ist der Innendienst. */
  async function laden() {
    if (!verwaltet) return;
    try {
      const [b, g, m] = await Promise.all([
        api.benutzer(), api.gruppen(), api.module(),
      ]);
      setBenutzer(b); setGruppen(g); setModule(m); setFehler(null);
    } catch (f: any) { setFehler(f.message); }
  }
  useEffect(() => { laden(); }, []);
  useFrisch(laden);

  if (fehler) {
    return <div className="inhalt"><div className="hinweis fehler">{fehler}</div></div>;
  }

  return (
    <>
      <Kopf
        titel="Benutzer und Rechte"
        weg={!verwaltet ? 'Firmen und Teams' : `${benutzer.length} Benutzer, ${gruppen.length} Gruppen, `
           + `${module.reduce((n, m) => n + m.rechte.length, 0)} Funktionen `
           + `in ${module.length} Modulen`}
        rechts={
          <div className="reihe">
            {(([
              ['benutzer', 'Benutzer'], ['gruppen', 'Gruppen'],
              ['firmen', 'Firmen und Teams'],
              ['rechte', 'Module und Funktionen'],
              ['sicherheit', 'Meine Anmeldung'],
            ] as [Reiter, string][]).filter(([k]) => verwaltet || k === 'firmen' || k === 'sicherheit')).map(([k, n]) => (
              <button key={k} className={'knopf' + (reiter === k ? ' haupt' : '')}
                      onClick={() => { setReiter(k); setGewaehlt(null); }}>
                {n}
              </button>
            ))}
          </div>
        }
      />
      <div className="inhalt">

        {reiter === 'benutzer' && (
          <BenutzerListe
            ich={ich} benutzer={benutzer} gruppen={gruppen} module={module}
            gewaehlt={gewaehlt} waehle={setGewaehlt}
            neuLaden={laden} uebernommen={uebernommen}
          />
        )}
        {reiter === 'gruppen' && (
          <GruppenListe gruppen={gruppen} module={module} neuLaden={laden} />
        )}
        {reiter === 'rechte' && <ModulListe module={module} gruppen={gruppen} />}
        {reiter === 'firmen' && <FirmenUndTeams ich={ich} benutzer={benutzer} />}
        {/* `ich` MUSS MIT. Ohne es steht das Feld „Mailadresse" leer da, und
            genau dieses Feld ist der Anker, an dem der Passwortverwalter des
            Browsers den Benutzernamen festmacht. Ein leerer Anker ist keiner:
            dann sucht Chrome sich wieder das nächstbeste Textfeld der Seite -
            das war der Fehler aus Marvins Bildschirmfoto. */}
        {reiter === 'sicherheit' && (
          <><MeineSicherheit ich={ich} /><FaktorUebersicht /></>
        )}
      </div>
    </>
  );
}


// ------------------------------------------------------- Firmen und Teams

/**
 * ALLE FIRMEN, TEAMS UND ZUGÄNGE AN EINEM ORT (.577).
 *
 * ORIGINALTEXT MARVIN (02.11.2026)
 *   „Willst du das nicht als Reiter in die Benutzerkontensteuerung ziehen so
 *    das wir alles zusammen haben. Auch die Firmen von ne3? Ich gehe das alles
 *    an mehreren stellen suchen und befinden nächsten Bereich der bald
 *    ansteht geht es da weiter mit dem Wirrwarr."
 *
 * Bis hier: NE3-Firmen unter Stammdaten, NE4-Firmen und Teams im Menü von
 * NE4, die Zugänge hier. Jetzt ein Reiter, je Bereich ein Abschnitt; ein
 * neuer Bereich bekommt hier seinen Abschnitt und keinen eigenen Menüpunkt.
 * Dieselben Bausteine wie vorher, nichts doppelt gespeichert.
 */
function FirmenUndTeams({ ich, benutzer }: { ich: Anmelder; benutzer: any[] }) {
  const ne3 = darf(ich, 'benutzer.verwalten') || darf(ich, 'stammdaten.pflegen');
  const ne4 = darf(ich, 'ne4.teams.pflegen') || darf(ich, 'ne4.lesen');
  const jeFirma = new Map<string, any[]>();
  for (const b of benutzer) if (b.firma) jeFirma.set(b.firma, [...(jeFirma.get(b.firma) ?? []), b]);
  return (
    <>
      {ne3 && (
        <Karte titel="NE3, Partnerfirmen">
          <p className="klein leise" style={{ marginTop: 0 }}>
            Die Firma am Zugang ist die Sichtbarkeitsgrenze in NE3: er sieht nur Aufträge, an denen diese Firma ein Gewerk hat. Zugeordnet wird beim Zugang („Zugang anlegen" oder „ändern").
          </p>
          <Firmen pflegen={darf(ich, 'stammdaten.pflegen')} />
          {jeFirma.size > 0 && (
            <>
              <h4 style={{ margin: '16px 0 6px' }}>Zugänge je Partnerfirma</h4>
              <table className="liste">
                <thead><tr><th>Firma</th><th>Zugänge</th></tr></thead>
                <tbody>
                  {[...jeFirma.entries()].sort((a, b) => a[0].localeCompare(b[0], 'de')).map(([f, l]) => (
                    <tr key={f} style={{ cursor: 'default' }}>
                      <td><b>{f}</b></td>
                      <td>{l.map((b) => b.name + (b.aktiv ? '' : ' (nicht aktiv)')).join(', ')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </Karte>
      )}
      {ne4 && (
        <Karte titel="NE4, Firmen und Teams">
          <p className="klein leise" style={{ marginTop: 0 }}>
            Ein Firmenzugang sieht die gebuchten Termine und Zahlen der Teams seiner Firma, ein Teamzugang nur die seines Teams. Zuordnen geht hier oder beim Zugang (Gruppe „NE4 Firmenzugang" oder „NE4 Teamzugang", darunter Firma bzw. Team).
          </p>
          <Ne4Teams ich={ich} eingebettet />
        </Karte>
      )}
      {!ne3 && !ne4 && <div className="leise">Keine Firmen in deinem Bereich.</div>}
    </>
  );
}


// ---------------------------------------------------------------- Benutzer

/**
 * Ein Passwort für jemand anderen setzen.
 *
 * ORIGINALTEXT MARVIN (24.08.2026)
 *   „Niemand bekommt einen extra Link. Alle loggen sich mit ihren Zugangsdaten
 *    ein und aufgrund der Zuordnung und Rechten sieht er dann das was er sehen
 *    muss."
 *
 * Hier stand der Anmeldelink-Dialog. Ohne ihn braucht es diesen: Passwörter
 * ließen sich nur auf der Kommandozeile setzen, und dorthin kommt der
 * Innendienst nicht.
 *
 * DAS PASSWORT WIRD NICHT ERFUNDEN, SONDERN EINGETIPPT. Ein vom System
 * gewürfeltes Passwort muss man vorlesen, und was man vorliest, schreibt der
 * andere auf einen Zettel. Wer es vergibt, spricht es ohnehin mit dem
 * Betreffenden ab.
 */
function PasswortDialog({
  benutzer, zu, fertig,
}: {
  benutzer: { id: string; name: string };
  zu: () => void;
  fertig: () => void;
}) {
  const [wert, setWert] = useState('');
  const [nochmal, setNochmal] = useState('');
  const [zeigen, setZeigen] = useState(false);
  const [arbeitet, setArbeitet] = useState(false);
  const [fehler, setFehler] = useState<Fehlerlage | null>(null);
  const [ergebnis, setErgebnis] = useState<number | null>(null);
  const [verstoesse, setVerstoesse] = useState<string[]>([]);

  /* DIE MINDESTLAENGE KOMMT VOM SERVER. Hier stand `10`, festverdrahtet, an
     zwei Stellen - und als die Vorgabe auf zwölf ging, hätte dieser Dialog
     ein Passwort angenommen, das der Server abweist. */
  const vorgabe = usePasswortvorgabe();
  const mindest = vorgabe?.mindestlaenge ?? 12;
  const kurz = wert.length > 0 && wert.length < mindest;
  const ungleich = nochmal.length > 0 && wert !== nochmal;
  const bereit = wert.length >= mindest && wert === nochmal;

  async function setzen() {
    setArbeitet(true); setFehler(null); setVerstoesse([]);
    try {
      const a = await api.passwortSetzen(benutzer.id, wert);
      setErgebnis(a.beendeteSitzungen);
      fertig();
    } catch (f: any) {
      setFehler(ausFehler(f));
      setVerstoesse(f?.daten?.verstoesse ?? []);
    } finally { setArbeitet(false); }
  }

  return (
    <Dialog titel="Passwort setzen" untertitel={"für " + benutzer.name}
            breite={560} laeuft={arbeitet} zu={zu}>
      <Fehlerkasten lage={fehler} />
      {ergebnis !== null ? (
        <>
          <div className="hinweis">
            <b>Das Passwort ist gesetzt.</b>
            <div className="klein" style={{ fontWeight: 400, marginTop: 4 }}>
              {ergebnis === 0
                ? 'Es lief keine Sitzung, die zu beenden gewesen wäre.'
                : `${ergebnis} laufende ${ergebnis === 1 ? 'Sitzung wurde'
                    : 'Sitzungen wurden'} beendet.`}
            </div>
          </div>
          <p className="klein leise">
            Geben Sie es {benutzer.name} persönlich weiter, nicht per Nachricht.
            Beim nächsten Anmelden wird {benutzer.name} aufgefordert, ein
            eigenes zu setzen - vorher geht nichts anderes.
          </p>

        </>
      ) : (
        <>
          {/* DIE VORGABE STEHT DA, BEVOR GETIPPT WIRD.
              Marvin, 27.08.2026: „das muss auch angezeigt werden bei jeder
              passwortvergabe." Auch hier - vielleicht gerade hier: wer ein
              Passwort für einen ANDEREN wählt, denkt nicht lange nach. */}
          <Passwortregeln wert={wert} verstoesse={verstoesse} />
          <p className="klein leise" style={{ marginTop: 8 }}>
            Ein gesetztes Passwort kennen danach 2 Menschen. Deshalb muss
            {' '}{benutzer.name} es beim nächsten Anmelden ändern, bevor er
            etwas anderes tun kann. <b>Besser als das Ansagen am Telefon ist
            eine Einladung</b> - dann kennt es niemand außer ihm.
          </p>
          <div className="feld">
            <label htmlFor="pw-neu">Neues Passwort</label>
            <input id="pw-neu" type={zeigen ? 'text' : 'password'}
                   value={wert} autoComplete="new-password"
                   onChange={(e) => setWert(e.target.value)} />
            {kurz && (
              <div className="klein rot">
                Noch {mindest - wert.length} Zeichen.
              </div>
            )}
          </div>
          <div className="feld">
            <label htmlFor="pw-nochmal">Neues Passwort wiederholen</label>
            <input id="pw-nochmal" type={zeigen ? 'text' : 'password'}
                   value={nochmal} autoComplete="new-password"
                   onChange={(e) => setNochmal(e.target.value)} />
            {/* ZWEIMAL EINGEBEN IST KEIN SCHIKANE. Ein Tippfehler im
                Passwortfeld sieht man nicht, und danach kommt der Betreffende
                nicht herein und niemand weiss warum. */}
            {ungleich && (
              <div className="klein rot">Die beiden sind nicht gleich.</div>
            )}
          </div>
          <label className="wahlzeile klein leise">
            <input type="checkbox" checked={zeigen}
                   onChange={(e) => setZeigen(e.target.checked)} />
            Passwort anzeigen
          </label>
          <div className="hinweis warnung" style={{ marginTop: 12 }}>
            Alle laufenden Sitzungen dieses Benutzers werden dabei beendet.
            <div className="klein" style={{ fontWeight: 400, marginTop: 4 }}>
              Ein neues Passwort, das die alten Sitzungen stehen lässt, ändert
              für den, der sie hat, gar nichts.
            </div>
          </div>
          <div className="reihe" style={{ marginTop: 12 }}>
            <div className="wachsen" />
            <button className="knopf" onClick={zu} disabled={arbeitet}>
              Abbrechen
            </button>
            <button className="knopf haupt" disabled={!bereit || arbeitet}
                    onClick={setzen}>
              {arbeitet ? 'Einen Moment' : 'Passwort setzen'}
            </button>
          </div>
        </>
      )}
    </Dialog>
  );
}

/**
 * Einen Zugang anlegen oder ändern.
 *
 * ORIGINALTEXT MARVIN (27.08.2026)
 *   „Wo ist das onboarding? Ich will, wenn ich Mitarbeiter anlege diese einem
 *    Partner zuweisen können? Somit hat ein Partner die Möglichkeit mit
 *    mehreren Benutzern da drauf zu arbeiten in seinem Bereich und seinen
 *    Aufträgen."
 *
 * DIESES FENSTER GAB ES NICHT, UND DEN WEG DAHINTER AUCH NICHT. Jede
 * Benutzerzeile dieses Systems stammt aus der Übernahme; im ganzen
 * Serverprogramm stand kein `INSERT INTO benutzer`. Die Liste ZÄHLTE sogar,
 * wie viele Zugänge an einer Firma hängen.
 *
 * DIE FIRMA STEHT MITTEN IM FORMULAR UND NICHT AM RAND. Sie ist keine
 * Stammangabe wie eine Telefonnummer: sie entscheidet, WELCHE BAUSTELLEN
 * dieser Mensch sieht. Beim Ändern verlangt der Server dafür eine
 * ausdrückliche Bestätigung, und alle Sitzungen des Betroffenen enden - eine
 * offene Sitzung trägt die alte Grenze sonst bis zum Abmelden mit sich.
 */
function ZugangDialog({ zeile, firmen, gruppen, zu, fertig }: any) {
  const neuerZugang = !zeile?.id;

  const [f, setF] = useState<any>({
    name: zeile?.name ?? '',
    email: zeile?.email ?? '',
    telefon: zeile?.telefon ?? '',
    telegram_chat_id: zeile?.telegram_chat_id ?? '',
    subunternehmer_id: zeile?.subunternehmer_id ?? '',
    aktiv: zeile?.aktiv ?? true,
    // Der Regelweg. Siehe unten, „Wie kommt er herein?"
    einladen: true,
  });
  /* DIE GRUPPEN GEHÖREN IN DIESEN DIALOG - AUCH BEIM ÄNDERN.
   *
   * ORIGINALTEXT MARVIN (31.08.2026)
   *   „und die „gruppen" zur zuordnung brauchen wir auch noch."
   *
   * ES GAB SIE, NUR NICHT HIER. Beim ANLEGEN standen die Häkchen im Dialog,
   * beim ÄNDERN in einer zweiten Karte weiter unten auf der Seite, die erst
   * erscheint, wenn man den Zugang in der Liste ANWÄHLT. Wer „ändern" klickt,
   * ändert alles ausser dem, was der Mensch tun darf - und sucht die Gruppen
   * dort, wo sie beim Anlegen standen.
   *
   * Beim Ändern kommt die Vorbelegung vom Server; `zeile.gruppen` sind die
   * NAMEN (die Liste zeigt sie an), gebraucht werden die Schlüssel. Deshalb
   * wird der Zugang beim Öffnen einmal einzeln geholt. */
  const [ausgewaehlt, setAusgewaehlt] = useState<string[]>([]);
  const [gruppenGeladen, setGruppenGeladen] = useState(neuerZugang);
  const [klar, setKlar] = useState(false);
  /* WARTET DIESER ZUGANG ERST EINMAL? (Migration 130)
     ------------------------------------------------------------------------
     ORIGINALTEXT MARVIN (03.09.2026)
       „ich will gerne, dass sich die Auftragnehmer regisrieren, aber sie noch
        nichts aktiv sehen oder bedienen können."

     VORBELEGT, SOBALD EINE FIRMA GEWAEHLT IST - und zwar SICHTBAR und nicht
     im Verborgenen. Ein Auftragnehmer richtet sich ein, bevor er zugeteilt
     wird; ein Zugang des Innendienstes soll sofort arbeiten. Der Haken steht
     da, der Mensch davor entscheidet: „Der Vorschlag ist ein Vorschlag."

     NUR BEIM ANLEGEN. Im Aenderungsfenster steht er nicht - dort gibt es
     dafuer den eigenen Knopf in der Liste, weil beim Freischalten eine Mail
     hinausgeht. Zwei Wege zu derselben Sache waeren eine halbe Auskunft. */
  const [wartetVonHand, setWartetVonHand] = useState(false);
  const [wartet, setWartetRoh] = useState(false);
  const setzeWartet = (v: boolean) => { setWartetRoh(v); setWartetVonHand(true); };
  const [arbeitet, setArbeitet] = useState(false);
  const [fehler, setFehler] = useState<Fehlerlage | null>(null);
  /* .577: DIE NE4-ZUORDNUNG STEHT HIER MIT. Marvin, 02.11.2026: „Ja baue das
     so. Wir dürfen den Überblick nicht verlieren und gleichzeitig auch nicht
     dezentral das ganze verwalten. Auch für die Teams ist das wichtig." */
  const [ne4Firmen, setNe4Firmen] = useState<any[]>([]);
  const [ne4Teams, setNe4Teams] = useState<any[]>([]);
  const [ne4Firma, setNe4Firma] = useState('');
  const [ne4Team, setNe4Team] = useState('');
  const [ne4Vorher, setNe4Vorher] = useState({ firma: '', team: '' });
  useEffect(() => {
    api.ne4Firmen().then(setNe4Firmen).catch(() => setNe4Firmen([]));
    api.ne4Teams().then(setNe4Teams).catch(() => setNe4Teams([]));
  }, []);

  useEffect(() => {
    if (neuerZugang || !zeile?.id) return;
    let weg = false;
    (async () => {
      try {
        const d = await api.einBenutzer(zeile.id);
        if (!weg) {
          setAusgewaehlt((d.gruppen ?? []).map((g: any) => g.schluessel));
          setGruppenGeladen(true);
          const nf = d.ne4?.firmenzugang?.firma_id ?? '', nt = d.ne4?.teamzugang?.team_id ?? '';
          setNe4Firma(nf); setNe4Team(nt); setNe4Vorher({ firma: nf, team: nt });
        }
      } catch {
        /* EIN FEHLGESCHLAGENER ABRUF DARF DIE GRUPPEN NICHT LEEREN. Sonst
           speicherte ein Klick auf „Speichern" einen Zugang OHNE Gruppen -
           und der sieht danach nichts mehr. Bleibt `gruppenGeladen` falsch,
           werden sie gar nicht erst mitgeschickt. */
        if (!weg) setGruppenGeladen(false);
      }
    })();
    return () => { weg = true; };
  }, [zeile?.id, neuerZugang]);

  const firmaGeaendert = !neuerZugang
    && (f.subunternehmer_id || '') !== (zeile?.subunternehmer_id ?? '');
  const bereit = f.name.trim() && /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(f.email)
    && (!firmaGeaendert || klar);

  /* Nur was zur gewählten Gruppe passt, geht mit: ohne „NE4 Firmenzugang" keine Firma, ohne „NE4 Teamzugang" kein Team. */
  const ne4FirmaWert = ausgewaehlt.includes('ne4_firma') ? ne4Firma : '';
  const ne4TeamWert = ausgewaehlt.includes('ne4_team') ? ne4Team : '';
  const nurNe4 = ausgewaehlt.length > 0 && ausgewaehlt.every((g: string) => g.startsWith('ne4_'));
  const ne4FirmaName = ne4TeamWert ? (ne4Teams.find((t: any) => t.id === ne4TeamWert)?.firma ?? '')
    : ne4FirmaWert ? (ne4Firmen.find((x: any) => x.id === ne4FirmaWert)?.name ?? '') : '';
  /* .586: die GBGS-Ressource des Teams gleich hier. Marvin: „Und dann müsste ich ja noch pro Team jetzt den
     GBGS-Benutzer auswählen ... oder wo ist das eingebaut." Sie steht auch unter Firmen und Teams (Team ändern),
     dort zusätzlich je Person. */
  const [ressourcen, setRessourcen] = useState<any[] | null>(null);
  const [teamBid, setTeamBid] = useState<string | null>(null);
  useEffect(() => { if (ausgewaehlt.includes('ne4_team') && ressourcen === null) api.ne4GbgsRessourcen().then(setRessourcen).catch(() => setRessourcen([])); }, [ausgewaehlt]); // eslint-disable-line react-hooks/exhaustive-deps
  const teamZeile = ne4Teams.find((t: any) => t.id === ne4TeamWert);
  const teamBidJetzt = teamBid ?? (teamZeile?.gbgs_ressource_bid ? String(teamZeile.gbgs_ressource_bid) : '');
  async function speichern() {
    setArbeitet(true); setFehler(null);
    try {
      if (neuerZugang) {
        const a = await api.benutzerAnlegen({
          ...f, subunternehmer_id: f.subunternehmer_id || null,
          wartet: wartetVonHand ? wartet : !!f.subunternehmer_id,
          gruppen: ausgewaehlt });
        /* WAS DER SERVER GEANTWORTET HAT, WANDERT MIT. Ob die Einladung
           wirklich hinausging, weiss nur er - im Testmodus geht sie an die
           Testadresse, und dann muss der Link auf dem Bildschirm stehen. */
        if (a?.id && (ne4FirmaWert || ne4TeamWert)) await api.benutzerNe4(a.id, { firma_id: ne4FirmaWert || null, team_id: ne4TeamWert || null });
        if (ne4TeamWert && teamBid !== null) await api.ne4TeamAendern(ne4TeamWert, { gbgs_ressource_bid: teamBid ? Number(teamBid) : null });
        fertig(a.id, f.name, a);
      } else {
        await api.benutzerAendern(zeile.id, {
          ...f, subunternehmer_id: f.subunternehmer_id || null,
          mir_ist_klar: klar });
        /* DIE GRUPPEN GEHEN ÜBER IHREN EIGENEN WEG, und das ist keine
           Umständlichkeit: `PATCH /benutzer/:id` ändert die Stammangaben,
           `PUT /benutzer/:id/gruppen` ersetzt die Zuordnung. Zwei Fragen,
           zwei Wege, zwei Protokollzeilen - wer sie zusammenlegt, kann später
           nicht mehr sagen, ob jemand umbenannt oder entrechtet wurde.
           NUR WENN SIE WIRKLICH GELADEN WURDEN. Sonst schickte ein
           fehlgeschlagener Abruf eine leere Liste, und der Zugang stünde
           danach ohne jede Gruppe da. */
        if (gruppenGeladen) await api.benutzerGruppen(zeile.id, ausgewaehlt);
        if (gruppenGeladen && (ne4FirmaWert !== ne4Vorher.firma || ne4TeamWert !== ne4Vorher.team))
          await api.benutzerNe4(zeile.id, { firma_id: ne4FirmaWert || null, team_id: ne4TeamWert || null });
        if (ne4TeamWert && teamBid !== null) await api.ne4TeamAendern(ne4TeamWert, { gbgs_ressource_bid: teamBid ? Number(teamBid) : null });
        fertig(null, f.name);
      }
    } catch (x) { setFehler(ausFehler(x)); } finally { setArbeitet(false); }
  }

  return (
    <Dialog titel={neuerZugang ? 'Zugang anlegen' : 'Zugang ändern'}
            untertitel={neuerZugang ? undefined : zeile.name}
            breite={620} laeuft={arbeitet} zu={zu}>
      <Fehlerkasten lage={fehler} />

      <div className="feldreihe">
        <Text name="name" titel="Name" wert={f.name} pflicht breite={260}
              setze={(v) => setF({ ...f, name: v })} fehler={fehler} />
        {/* DIE MAILADRESSE IST DER ANMELDENAME. Ohne sie gibt es keinen Weg
            herein: „Niemand bekommt einen extra Link." */}
        <Text name="email" titel="Mailadresse" wert={f.email} pflicht
              breite={280} hinweis="Damit meldet er sich an."
              setze={(v) => setF({ ...f, email: v })} fehler={fehler} />
        <Text name="telefon" titel="Telefon" wert={f.telefon} breite={180}
              setze={(v) => setF({ ...f, telefon: v })} />
        <Text name="telegram_chat_id" titel="Telegram-Chat" breite={180}
              wert={f.telegram_chat_id}
              hinweis="Für die Meldung jeder Anmeldung. Die Nummer nennt der
                       Bot @userinfobot."
              setze={(v) => setF({ ...f, telegram_chat_id: v })} />
      </div>

      {/* .586: NUR NE4: KEINE NE3-FIRMA. Marvin, 02.11.2026: „Warum steht da Firma Innendienst und nicht
          die zugewiesene Firma ..., auch wenn es eine NE4-Firma ist". Die NE3-Firma ist die Sichtbarkeits-
          grenze in NE3; wer nur NE4-Gruppen hat, bekommt seine Firma über Team oder Firmenzugang unten. */}
      {nurNe4 ? (
        <p className="klein" style={{ marginTop: 4 }}>
          <b>Firma:</b> {ne4FirmaName || <span className="leise">noch keine</span>} <span className="leise">, in NE4 über {ausgewaehlt.includes('ne4_team') ? 'das Team' : 'den Firmenzugang'} unten. Eine NE3-Firma braucht dieser Zugang nicht.</span>
        </p>
      ) : (<>
      <label className="feld" style={{ maxWidth: 380 }}>
        <span>Firma</span>
        <select value={f.subunternehmer_id}
                onChange={(e) => setF({ ...f, subunternehmer_id: e.target.value })}>
          <option value="">Innendienst (sieht alle Aufträge)</option>
          {firmen.filter((s: any) => s.aktiv).map((s: any) => (
            <option key={s.id} value={s.id}>{s.firma}</option>
          ))}
        </select>
      </label>
      <p className="klein leise" style={{ marginTop: 4 }}>
        <b>Die Firma ist die Sichtbarkeitsgrenze.</b> Wer einer Firma zugeordnet
        ist, sieht ausschließlich Aufträge, an denen diese Firma ein Gewerk
        hat - und an einer Firma dürfen beliebig viele Zugänge hängen.
      </p>
      </>)}

      {/* Der Vorschlag folgt der Firma, solange niemand von Hand entschieden
          hat. Wer den Haken anfasst, hat entschieden - danach bleibt seine
          Entscheidung stehen, auch wenn er die Firma noch einmal wechselt. */}
      {neuerZugang && (
        <div className="hinweis" style={{ marginTop: 10 }}>
          <label className="wahlzeile">
            <input type="checkbox"
                   checked={wartetVonHand ? wartet : !!f.subunternehmer_id}
                   onChange={(e) => setzeWartet(e.target.checked)} />
            <b>Wartet auf Freischaltung</b>
          </label>
          <div className="klein" style={{ fontWeight: 400, marginTop: 4 }}>
            Er kommt herein und kann sein Passwort und die Anmeldung in 2
            Schritten einrichten - und sieht sonst nichts: kein Menü, keinen
            Auftrag, keine Zahl. Freischalten geht danach mit einem Knopf in
            der Liste.
          </div>
        </div>
      )}

      {firmaGeaendert && (
        <div className="hinweis warnung" style={{ marginTop: 10 }}>
          Diese Änderung verschiebt, was {zeile.name} sieht.
          <div className="klein" style={{ fontWeight: 400, marginTop: 4 }}>
            Alle laufenden Sitzungen enden dabei: eine offene Sitzung trägt die
            alte Grenze sonst bis zum Abmelden mit sich.
          </div>
          <label className="wahlzeile klein" style={{ marginTop: 6 }}>
            <input type="checkbox" checked={klar}
                   onChange={(e) => setKlar(e.target.checked)} />
            Mir ist klar, was das heißt.
          </label>
        </div>
      )}

      {/* DIE GRUPPEN STEHEN BEI BEIDEN WEGEN, ANLEGEN WIE ÄNDERN.
        *
        * ORIGINALTEXT MARVIN (31.08.2026)
        *   „und die „gruppen" zur zuordnung brauchen wir auch noch."
        *
        * Beim Anlegen standen sie hier, beim Ändern in einer zweiten Karte
        * weiter unten, die erst erscheint, wenn man den Zugang in der Liste
        * ANWÄHLT. Zwei Orte für dieselbe Frage, und der zweite war der, den
        * niemand findet.
        *
        * DIE FIRMA UND DIE GRUPPE SIND ZWEI VERSCHIEDENE FRAGEN, und deshalb
        * steht der Satz darunter: die Firma sagt, WELCHE Aufträge jemand
        * sieht, die Gruppe sagt, WAS er damit tun darf. Wer das verwechselt,
        * gibt einem Monteur die Innendienstgruppe und wundert sich, dass er
        * trotzdem nur seine Baustellen sieht. */}
      <h4 style={{ margin: '14px 0 6px' }}>Gruppen</h4>
      {!gruppenGeladen && !neuerZugang ? (
        <p className="klein leise">
          Die Gruppen dieses Zugangs ließen sich nicht laden. Sie werden
          deshalb auch nicht geändert.
        </p>
      ) : (
        <>
          <div className="reihe">
            {gruppen.map((g: any) => (
              <label key={g.id} className="gewerk" style={{ cursor: 'pointer' }}
                     title={g.beschreibung ?? ''}>
                <input type="checkbox"
                       checked={ausgewaehlt.includes(g.schluessel)}
                       onChange={(e) => setAusgewaehlt(e.target.checked
                         ? [...ausgewaehlt, g.schluessel]
                         : ausgewaehlt.filter((x) => x !== g.schluessel))} />
                {' '}{g.name}
                {/* DER BEREICH DER GRUPPE (.333): NE3, NE4 oder alle. */}
                <span className="klein leise">, {g.bereich ? String(g.bereich).toUpperCase() : 'alle Bereiche'}</span>
              </label>
            ))}
          </div>
          <p className="klein leise" style={{ marginTop: 4 }}>
            Die Gruppe sagt, WAS er tun darf. Die Firma darüber sagt, WELCHE
            Aufträge er dabei sieht.
            {ausgewaehlt.length === 0 && (
              <> <b>Ohne Gruppe kann er sich anmelden und sonst nichts.</b></>
            )}
          </p>
          {ausgewaehlt.includes('ne4_firma') && (
            <label className="feld" style={{ maxWidth: 380, marginTop: 8 }}>
              <span>NE4-Firma</span>
              <select value={ne4Firma} onChange={(e) => setNe4Firma(e.target.value)}>
                <option value="">keine (sieht in NE4 nichts)</option>
                {ne4Firmen.filter((x: any) => x.aktiv || x.id === ne4Firma).map((x: any) => <option key={x.id} value={x.id}>{x.name} ({x.kurz})</option>)}
              </select>
              <span className="klein leise" style={{ fontWeight: 400 }}>Sieht die gebuchten Termine und Zahlen der Teams dieser Firma.</span>
            </label>
          )}
          {ausgewaehlt.includes('ne4_team') && (
            <label className="feld" style={{ maxWidth: 380, marginTop: 8 }}>
              <span>NE4-Team</span>
              <select value={ne4Team} onChange={(e) => setNe4Team(e.target.value)}>
                <option value="">kein Team (sieht in NE4 nichts)</option>
                {ne4Teams.filter((t: any) => t.aktiv || t.id === ne4Team).map((t: any) => (
                  <option key={t.id} value={t.id} disabled={!!t.benutzer_id && t.benutzer_id !== zeile?.id}>
                    {t.firma_kurz} {t.name}{t.benutzer_id && t.benutzer_id !== zeile?.id ? ` (Zugang: ${t.zugang_name})` : ''}
                  </option>
                ))}
              </select>
              <span className="klein leise" style={{ fontWeight: 400 }}>Ein Team hat genau einen Zugang; er sieht nur die Termine dieses Teams.</span>
            </label>
          )}
          {ausgewaehlt.includes('ne4_team') && ne4TeamWert && (
            <label className="feld" style={{ maxWidth: 380, marginTop: 8 }}>
              <span>GBGS-Ressource des Teams</span>
              <select value={teamBidJetzt} onChange={(e) => setTeamBid(e.target.value)}>
                <option value="">keine</option>
                {(ressourcen ?? []).map((r: any) => <option key={r.bid} value={String(r.bid)}>{r.name}{r.faehigkeit ? ` (${r.faehigkeit})` : ''}</option>)}
              </select>
              <span className="klein leise" style={{ fontWeight: 400 }}>Wer bei einem Termin dieses Teams im GBGS gebucht wird. Je Mitarbeiter geht es unter Firmen und Teams, Team ändern, ✎ an der Person.{ressourcen && !ressourcen.length ? ' Die Liste kommt aus dem GBGS-Lesen und ist noch leer.' : ''}</span>
            </label>
          )}
        </>
      )}

      {neuerZugang && (
        <>

          {/* ZWEI WEGE, UND DER ERSTE IST DER BESSERE.
              Marvin, 27.08.2026: „wenn ich einem neuen benutzer anlege dann
              soll er eine email erhalten, wo ein link drin ist, wo er sein
              passwort setzen muss." */}
          <h4 style={{ margin: '14px 0 6px' }}>Wie kommt er herein?</h4>
          <label className="wahlzeile klein">
            <input type="radio" checked={f.einladen}
                   onChange={() => setF({ ...f, einladen: true })} />
            <span>
              <b>Einladung senden.</b> Er bekommt eine Mail mit einem Link und
              setzt sein Passwort selbst - niemand hier kennt es dann.
            </span>
          </label>
          <label className="wahlzeile klein" style={{ marginTop: 6 }}>
            <input type="radio" checked={!f.einladen}
                   onChange={() => setF({ ...f, einladen: false })} />
            <span>
              <b>Passwort hier setzen.</b> Danach kennen es 2 Menschen, und
              er muss es beim nächsten Anmelden ändern.
            </span>
          </label>
          <div className="hinweis klein" style={{ marginTop: 10 }}>
            <b>Der Einladungslink meldet niemanden an.</b>
            <div style={{ fontWeight: 400, marginTop: 4 }}>
              Er kann genau eine Sache: ein Passwort setzen. Danach ist er
              verbraucht, und der Weg herein bleibt Mailadresse und Passwort -
              für jeden. Der Anmeldelink aus dem alten System bleibt
              abgeschafft.
            </div>
          </div>
        </>
      )}

      {!neuerZugang && (
        <label className="wahlzeile klein" style={{ marginTop: 10 }}>
          <input type="checkbox" checked={f.aktiv}
                 onChange={(e) => setF({ ...f, aktiv: e.target.checked })} />
          Zugang ist aktiv
        </label>
      )}

      <div className="reihe" style={{ marginTop: 12 }}>
        {/* LÖSCHEN (.461). Marvin, 22.10.2026: „Ich kann keine Benutzer
            löschen." Der Zugang geht aus der Liste und kann sich nicht mehr
            anmelden; was er geschrieben hat, bleibt mit seinem Namen stehen. */}
        {!neuerZugang && (
          <button className="knopf warnung" disabled={arbeitet} onClick={async () => {
            if (!await fragen(`${zeile.name} wirklich löschen?\n\nDer Zugang verschwindet aus der Liste und kann sich nicht mehr anmelden. Laufende Sitzungen enden, Gruppen und Rechte fallen weg, die Mailadresse ist danach frei. Was ${zeile.name} in Aufträgen eingetragen hat, bleibt mit dem Namen stehen.\n\nDas lässt sich hier nicht rückgängig machen.`)) return;
            setArbeitet(true); setFehler(null);
            try { await api.benutzerLoeschen(zeile.id); fertig(null, zeile.name); }
            catch (x) { setFehler(ausFehler(x)); } finally { setArbeitet(false); }
          }}>Benutzer löschen</button>
        )}
        <div className="wachsen" />
        <button className="knopf" onClick={zu} disabled={arbeitet}>
          Abbrechen
        </button>
        <button className="knopf haupt" disabled={!bereit || arbeitet}
                onClick={speichern}>
          {neuerZugang
            ? (f.einladen ? 'Anlegen und einladen' : 'Anlegen und Passwort setzen')
            : 'Speichern'}
        </button>
      </div>
    </Dialog>
  );
}


/**
 * Was aus einer Einladung geworden ist.
 *
 * ORIGINALTEXT MARVIN (27.08.2026)
 *   „wenn ich einem neuen benutzer anlege dann soll er eine email erhalten, wo
 *    ein link drin ist, wo er sein passwort setzen muss."
 *
 * DIESES FENSTER IST DIE EHRLICHKEIT ZUM TESTMODUS. Solange
 * `betrieb.testmodus` auf „ja" steht, geht die Mail an die Testadresse und
 * NICHT an den Eingeladenen. Ein Fenster, das dann „ist unterwegs" meldet,
 * hätte die Wahrheit gesagt und den Menschen im Regen stehen lassen: er wartet
 * auf eine Mail, die er nie bekommt.
 *
 * DER LINK STEHT DESHALB IMMER DA, auch wenn die Mail hinausging. Er lässt
 * sich in einem Zug kopieren und am Telefon nicht vorlesen - dafür ist er zu
 * lang, und genau das ist gut so: ein Link, den man vorlesen kann, ist ein
 * Passwort.
 */
function EinladungDialog({ e, zu }: { e: any; zu: () => void }) {
  const [kopiert, setKopiert] = useState(false);
  return (
    <Dialog titel="Einladung" untertitel={'für ' + (e.name ?? '')}
            breite={640} zu={zu}>
      {/* DREI ZUSTÄNDE, NICHT ZWEI.
          Marvin, 27.08.2026, mit zwei Bildschirmfotos: „die rückmeldung auf
          bild 2 ist ja nicht korrekt." Auf dem einen lag die Mail im
          Postfach, auf dem anderen stand hier „Die Mail ist NICHT
          hinausgegangen. n8n: HTTP 200". Beides zugleich, und der zweite Satz
          war falsch: die Mail IST hinausgegangen, nur an die Testadresse.
          „HTTP 200" als Grund eines Scheiterns zu lesen war die Umkehrung
          dessen, was dastand.

          Ein umgeleiteter Versand ist kein Fehler, sondern eine ANSAGE über
          den Testmodus - deshalb kein Warnkasten, sondern ein neutraler, und
          er sagt, wo der Schalter steht. */}
      <div className={'hinweis '
                      + (e.ausgang === 'gescheitert' ? 'warnung' : '')}>
        {e.ausgang === 'zugestellt' && (
          <>
            <b>Die Mail ist unterwegs.</b>
            <div className="klein" style={{ fontWeight: 400, marginTop: 4 }}>
              An {e.email ?? 'die hinterlegte Adresse'}. Der Link gilt
              {' '}{e.stunden} Stunden und lässt sich genau einmal benutzen.
            </div>
          </>
        )}
        {e.ausgang === 'umgeleitet' && (
          <>
            <b>Die Mail ist hinausgegangen - an die Testadresse.</b>
            <div className="klein" style={{ fontWeight: 400, marginTop: 4 }}>
              {e.grund}{' '}
              <b>{e.email ?? 'Der Empfänger'} hat sie also nicht bekommen.</b>
              {' '}Der Link steht unten und lässt sich von Hand weitergeben.
              Umgestellt wird das unter „Konfiguration", Reiter „Betrieb",
              Schlüssel <code>betrieb.testmodus</code>.
            </div>
          </>
        )}
        {e.ausgang === 'gescheitert' && (
          <>
            <b>Die Mail ist NICHT hinausgegangen.</b>
            <div className="klein" style={{ fontWeight: 400, marginTop: 4 }}>
              {e.grund ?? 'Der Versand hat nicht geantwortet.'}{' '}
              Der Link steht unten und lässt sich von Hand weitergeben.
            </div>
          </>
        )}
        {/* DER VIERTE FALL, UND ER IST DER GRUND FÜR DIESEN ABSATZ.
            Marvin, 28.08.2026: „oben ist noch der gelbe rahmen ohne inhalt."
            Ein Kasten, der drei Zustände kennt, bekommt irgendwann einen
            vierten - hier vom Weg „Zugang anlegen", der `ausgang` nicht
            mitgab. Ein Rahmen ohne Inhalt ist die schlechteste aller
            Auskünfte: er behauptet, es stünde etwas da.
            Er sagt jetzt, was er weiss, und was er nicht weiss. */}
        {!['zugestellt', 'umgeleitet', 'gescheitert'].includes(e.ausgang) && (
          <>
            <b>Die Einladung ist erzeugt.</b>
            <div className="klein" style={{ fontWeight: 400, marginTop: 4 }}>
              {e.versendet
                ? `Sie ist an ${e.email ?? 'die hinterlegte Adresse'} hinausgegangen.`
                : 'Ob die Mail hinausgegangen ist, hat dieser Weg nicht '
                  + 'zurückgemeldet.'}
              {' '}Der Link unten gilt {e.stunden ?? 48} Stunden und lässt sich
              genau einmal benutzen.
            </div>
          </>
        )}
      </div>

      <div className="feld">
        <label>Der Link</label>
        <input readOnly value={e.link ?? ''} className="einfarbig"
               onFocus={(x) => x.currentTarget.select()}
               style={{ width: '100%' }} />
      </div>
      <div className="reihe" style={{ marginTop: 8 }}>
        <button className="knopf klein"
                onClick={() => {
                  navigator.clipboard?.writeText(e.link ?? '')
                    .then(() => setKopiert(true)).catch(() => {});
                }}>
          {kopiert ? 'kopiert' : 'Link kopieren'}
        </button>
        <div className="wachsen" />
        <button className="knopf haupt" onClick={zu}>Fertig</button>
      </div>

      <p className="klein leise" style={{ marginTop: 12 }}>
        <b>Dieser Link meldet niemanden an.</b> Er kann genau eine Sache: ein
        Passwort setzen. Danach ist er verbraucht, und der Weg herein bleibt
        Mailadresse und Passwort. Eine neue Einladung zieht diese hier
        zurück.
      </p>
    </Dialog>
  );
}


function BenutzerListe({
  ich, benutzer, gruppen, module, gewaehlt, waehle, neuLaden, uebernommen,
}: any) {
  const [d, setD] = useState<any>(null);
  const [arbeitet, setArbeitet] = useState(false);
  const [meldung, setMeldung] = useState<string | null>(null);
  const [erledigt, setErledigt] = useState<string | null>(null);
  const [pw, setPw] = useState<{ id: string; name: string } | null>(null);
  // Was aus einer Einladung geworden ist. Siehe `EinladungDialog`.
  const [einladung, setEinladung] = useState<any>(null);
  // Welcher Zugang gerade angelegt oder geändert wird.
  const [zugang, setZugang] = useState<any>(null);
  // Die Firmen für die Zuordnung. Ohne sie liesse sich ein Zugang nur als
  // Innendienst anlegen, und genau die Zuordnung war die Frage.
  const [firmen, setFirmen] = useState<any[]>([]);

  useEffect(() => {
    api.subunternehmer().then(setFirmen).catch(() => setFirmen([]));
  }, []);

  useEffect(() => {
    if (!gewaehlt) { setD(null); return; }
    api.einBenutzer(gewaehlt).then((x) => {
      setD(x);
      /* .576: die aufgeklappte Zeile nach oben holen, die Rechte stehen direkt darunter. */
      requestAnimationFrame(() => document.querySelector('tr.gewaehlt')?.scrollIntoView({ block: 'start', behavior: 'smooth' }));
    }).catch((f) => setMeldung(f.message));
  }, [gewaehlt]);

  async function faktorZurueck(x: any) {
    if (!await fragen(`Die Authenticator-App von ${x.name} zurücksetzen?\n\nEr meldet sich danach mit Mail und Passwort an und scannt einen neuen QR-Code. Den alten Eintrag „MDS FTTH" in seiner App soll er vorher löschen.`)) return;
    setArbeitet(true); setMeldung(null);
    try {
      const r = await api.faktorZuruecksetzen(x.id);
      setErledigt(r.klartext);
      await neuLaden();
    } catch (f: any) { setMeldung(f.message); } finally { setArbeitet(false); }
  }

  async function gruppenSetzen(ids: string[]) {
    setArbeitet(true); setMeldung(null);
    try {
      await api.benutzerGruppen(gewaehlt, ids);
      setD(await api.einBenutzer(gewaehlt));
      await neuLaden();
    } catch (f: any) { setMeldung(f.message); } finally { setArbeitet(false); }
  }

  /* ------------------------------------------------ Rechte EINER PERSON
   *
   * ORIGINALTEXT MARVIN (07.09.2026)
   *   „dann ist mir aufgefalen, dass rechte in den Gruppen und
   *    menü/benutzern fehlen, die ich garnicht selektieren kann."
   *
   * BEI DEN BENUTZERN LIESS SICH GAR NICHTS SELEKTIEREN, und das war
   * wörtlich zu nehmen: das Raster „Wirksame Rechte" bekam kein `aendern`,
   * also stand jedes Kästchen auf `disabled`. `benutzer_recht` gibt es seit
   * Migration 001, `PUT /benutzer/:id/rechte` seit ebenso lange, und
   * `api.benutzerRechte` steht in `api.ts` - **gerufen hat es keine einzige
   * Zeile der Oberfläche.** Die Karte „Ausnahmen" darunter erschien nur,
   * wenn schon welche dastanden; entstehen konnte keine.
   *
   * Es ist derselbe Befund wie beim mehrtägigen Tiefbau (03.09.2026): **ein
   * Weg, den der Server kann und keine Oberfläche geht, ist von einem nicht
   * gebauten nicht zu unterscheiden** - und er sieht im Programmcode
   * vollständig aus, weil die teure Hälfte dasteht.
   *
   * WAS EIN KLICK HIER BEDEUTET, IST ETWAS ANDERES ALS IN DER
   * GRUPPENMASKE. Dort wird ein Recht der GRUPPE gegeben; hier entsteht eine
   * AUSNAHME für diesen einen Menschen. Drei Fälle, und der dritte ist der,
   * den man am häufigsten braucht:
   *
   *   aus der Gruppe, Haken weg     -> entzogen (`erlaubt: false`)
   *   nicht in der Gruppe, Haken an -> zusätzlich (`erlaubt: true`)
   *   schon eine Ausnahme, Klick    -> die Ausnahme fällt, es gilt wieder
   *                                    die Gruppe
   *
   * DER GRUND IST PFLICHT, und zwar nicht erst im Server: er verlangt ihn
   * für JEDE Zeile, und ein Formular, das ihn nicht fragt, läuft in eine
   * Fehlermeldung, in der zehn Rechte aufgezählt stehen. Er steht deshalb
   * neben dem Speichern-Knopf und sperrt ihn, solange er leer ist - mit dem
   * Satz warum, denn ein gesperrter Knopf ohne Grund sieht aus wie ein
   * kaputter Knopf.
   */
  const [rechteEntwurf, setRechteEntwurf] =
    useState<{ recht: string; erlaubt: boolean; grund: string }[] | null>(null);
  const [rechteGrund, setRechteGrund] = useState('');

  /* Der Entwurf entsteht aus dem, was WIRKLICH dasteht - und zwar erst beim
     ersten Klick. Ein Entwurf, der schon beim Öffnen der Karte anfängt,
     überlebt den Wechsel zum nächsten Zugang und schreibt dessen Ausnahmen
     auf einen fremden Menschen. */
  useEffect(() => { setRechteEntwurf(null); setRechteGrund(''); }, [gewaehlt]);

  const ausnahmenJetzt: { recht: string; erlaubt: boolean; grund: string }[] =
    rechteEntwurf ?? (d?.ausnahmen ?? []).map((a: any) => ({
      recht: a.recht_schluessel, erlaubt: a.erlaubt, grund: a.grund ?? '',
    }));

  /** Kommt dieses Recht aus einer Gruppe? (Nicht: „gilt es".) */
  function ausGruppe(recht: string): boolean {
    const w = (d?.wirksam ?? []).find((x: any) => x.recht_schluessel === recht);
    if (w && w.herkunft && w.herkunft !== 'einzeln erteilt') return true;
    /* EIN ENTZOGENES RECHT STEHT NICHT IN `wirksam` - es ist ja weg. Ob es
       aus einer Gruppe KÄME, sagt dann nur die Gruppe selbst. Ohne diese
       Zeile liesse sich ein Entzug nie zurücknehmen: der Klick hielte das
       Recht für ein neues und gäbe es „zusätzlich". */
    const meine = (d?.gruppen ?? []).map((g: any) => g.id);
    return gruppen.some((g: any) => meine.includes(g.id)
                                 && liste(g.rechte).includes(recht));
  }

  function rechtSchalten(recht: string) {
    const jetzt = [...ausnahmenJetzt];
    const i = jetzt.findIndex((a) => a.recht === recht);
    if (i >= 0) { jetzt.splice(i, 1); }          // zurück zur Gruppe
    else jetzt.push({ recht, erlaubt: !ausGruppe(recht), grund: '' });
    setRechteEntwurf(jetzt);
  }

  /* EIN GANZES MODUL AUF EINMAL - denselben Knopf hat die Gruppenmaske seit
     jeher, und hier fehlte er. Wer einem Vertreter „Konfiguration" geben
     will, setzt sonst jeden Haken einzeln, und ein Menüpunkt erscheint erst
     mit dem ERSTEN Recht: bis dahin sieht es aus, als täte das Klicken
     nichts.

     GESCHALTET WIRD NUR, WAS SICH ÄNDERT. Ein Recht, das der Mensch schon
     hat, bekommt keine Ausnahme - eine Zeile, die genau das erlaubt, was die
     Gruppe ohnehin trägt, ist die Sorte Eintrag, die in drei Monaten niemand
     mehr einordnen kann. */
  function modulSchalten(rechte: string[], an: boolean) {
    const jetzt = [...ausnahmenJetzt];
    for (const recht of rechte) {
      const hat = wirksamJetzt.has(recht);
      if (hat === an) continue;
      const i = jetzt.findIndex((a) => a.recht === recht);
      if (i >= 0) jetzt.splice(i, 1);
      else jetzt.push({ recht, erlaubt: an, grund: '' });
    }
    setRechteEntwurf(jetzt);
  }

  async function rechteSpeichern() {
    setArbeitet(true); setMeldung(null);
    try {
      await api.benutzerRechte(gewaehlt, ausnahmenJetzt.map((a) => ({
        ...a, grund: a.grund?.trim() ? a.grund : rechteGrund.trim(),
      })));
      setD(await api.einBenutzer(gewaehlt));
      setRechteEntwurf(null); setRechteGrund('');
      await neuLaden();
    } catch (f: any) { setMeldung(f.message); } finally { setArbeitet(false); }
  }

  /* Was am Ende gilt: die Gruppenrechte, plus die zusätzlich erteilten,
     minus die entzogenen. Gerechnet wird hier nur die ANZEIGE des Entwurfs -
     die Wahrheit steht in `v_benutzer_recht`, und nach dem Speichern kommt
     sie von dort zurück. */
  const wirksamJetzt = (() => {
    const aus = new Set<string>(
      (d?.wirksam ?? []).map((w: any) => w.recht_schluessel));
    if (!rechteEntwurf) return aus;
    for (const a of (d?.ausnahmen ?? [])) {           // erst die alten weg
      if (a.erlaubt) aus.delete(a.recht_schluessel);
      else if (ausGruppe(a.recht_schluessel)) aus.add(a.recht_schluessel);
    }
    for (const a of rechteEntwurf) {                  // dann die neuen drauf
      if (a.erlaubt) aus.add(a.recht); else aus.delete(a.recht);
    }
    return aus;
  })();

  const grundFehlt = ausnahmenJetzt.some((a) => !a.grund?.trim())
                     && !rechteGrund.trim();

  /**
   * Login als ein anderer Benutzer.
   *
   * Marvin, 23.08.2026: „beim hineinversetzen bzw. umbennen in „Login als...“
   * brauchen wir keinen Grund oder Protokoll."
   *
   * Also ohne Rueckfrage. Wer das Recht hat, darf es, und der Balken oben
   * sagt jederzeit, als wer man gerade unterwegs ist.
   */
  async function hinein(id: string, name: string) {
    setArbeitet(true); setMeldung(null);
    try {
      await api.loginAls(id);
      uebernommen?.();
    } catch (f: any) { setMeldung(f.message); setArbeitet(false); }
  }

  /**
   * Eine Einladung versenden.
   *
   * WAS DABEI HERAUSKOMMT, WIRD GEZEIGT UND NICHT NUR GEMELDET. Im Testmodus
   * geht die Mail an die Testadresse und nicht an den Eingeladenen; dann muss
   * der Link auf diesem Bildschirm stehen. Ein „Einladung versendet", das
   * nichts versendet hat, ist die schlimmste Sorte Meldung: sie stimmt und
   * hilft niemandem.
   */
  async function lade(x: any) {
    setArbeitet(true); setMeldung(null);
    try {
      const a = await api.einladungSenden(x.id);
      setEinladung({ ...a, name: x.name });
      await neuLaden();
    } catch (f: any) { setMeldung(f.message); } finally { setArbeitet(false); }
  }

  /* DER SCHALTER FUER DIE WARTESEITE (Migration 130).
     ------------------------------------------------------------------------
     ORIGINALTEXT MARVIN (03.09.2026)
       „ich will gerne, dass sich die Auftragnehmer regisrieren, aber sie noch
        nichts aktiv sehen oder bedienen können."

     ER WIRKT SOFORT, AUCH IN EINER OFFENEN SITZUNG - der Riegel im Server
     liest bei jeder Anfrage neu. Und die Antwort sagt, ob die Mail hinausging:
     ein „freigeschaltet", das den Betroffenen nicht erreicht, laesst ihn
     weiter warten. */
  async function freischaltung(x: any, wartet: boolean) {
    setArbeitet(true); setMeldung(null);
    try {
      const a = await api.benutzerFreischaltung(x.id, wartet);
      await neuLaden();
      if (a?.nichts) { setMeldung(a.hinweis ?? null); return; }
      setMeldung(wartet
        ? `${x.name} sieht ab sofort nur noch die Warteseite.`
        : `${x.name} ist freigeschaltet.`
          + (a?.mail
              ? (a.mail.gesendet
                  ? (a.mail.umgeleitet
                      ? ' Die Mail ging an die Testadresse (Testmodus).'
                      : ' Eine Mail ist hinausgegangen.')
                  : ` Die Mail ging NICHT hinaus: ${a.mail.grund ?? 'unbekannt'}`)
              : ' Ohne Mailadresse konnte niemand benachrichtigt werden.'));
    } catch (f: any) { setMeldung(f.message); } finally { setArbeitet(false); }
  }

  const kannUebernehmen = darf(ich, 'benutzer.hineinversetzen');
  // EIN EIGENES RECHT, nicht `benutzer.verwalten`. Wer Gruppen zuteilen darf,
  // darf damit nicht automatisch in fremde Zugänge.
  const kannPasswort = darf(ich, 'benutzer.passwort');
  /* EIN EIGENES, GEFÄHRLICHES RECHT (Migration 049). Die Firma an einem
     Zugang IST die Sichtbarkeitsgrenze: wer sie setzt, entscheidet, welche
     Baustellen dieser Mensch sieht. Das ist etwas anderes als Gruppen
     verteilen. */
  const kannAnlegen = darf(ich, 'benutzer.anlegen');

  return (
    <>
      {erledigt && <div className="hinweis erledigt">{erledigt}</div>}
      {meldung && <div className="hinweis fehler">{meldung}</div>}

      {pw && (
        <PasswortDialog benutzer={pw} zu={() => setPw(null)}
                        fertig={neuLaden} />
      )}

      {einladung && (
        <EinladungDialog e={einladung} zu={() => setEinladung(null)} />
      )}

      {zugang && (
        <ZugangDialog zeile={zugang} firmen={firmen} gruppen={gruppen}
                      zu={() => setZugang(null)}
                      fertig={async (id: string, name: string, a?: any) => {
                        setZugang(null);
                        await neuLaden();
                        if (!id) return;
                        /* ZWEI AUSGAENGE, JE NACHDEM, WAS GEWAEHLT WAR.
                           Wurde eingeladen, zeigt das nächste Fenster, ob die
                           Mail hinausging - und den Link, falls nicht. Wurde
                           nicht eingeladen, geht gleich das Passwortfenster
                           auf: ohne Passwort kommt niemand herein, und ein
                           Zugang, der heute angelegt und morgen
                           freigeschaltet wird, ist ein Mensch, der am
                           Montagmorgen anruft. */
                        if (a?.eingeladen) setEinladung({ ...a, name });
                        else setPw({ id, name });
                      }} />
      )}

      <Karte titel={`Benutzer (${benutzer.length})`}
             rechts={kannAnlegen ? (
               <button className="knopf haupt"
                       onClick={() => setZugang({ neu: true })}>
                 Zugang anlegen
               </button>
             ) : undefined}>
        <table className="liste">
          <thead>
            <tr>
              {/* DIE 2FA-SPALTE IST NEU (042) UND NIMMT PLATZ WEG. Die
                  anderen Breiten sind deshalb um denselben Betrag geschrumpft:
                  sonst bleibt für „Name" nichts übrig, und dort steht der
                  längste Text der Zeile - „Bauleiter Brester Bau" stand danach
                  auf drei Zeilen. */}
              {/* DIE KNOPFSPALTE IST SEIT DEM 27.08.2026 ZWANZIG PIXEL
                  BREITER, und Mailadresse und Gruppen geben sie ab: bei 190
                  Pixeln passte die Leiste in genau EINE Rasterspalte, und
                  dann stuenden fuenf Knoepfe untereinander. Bei 220 abzüglich
                  der Zellenpolster sind es zwei Spalten - die Zeile bleibt
                  drei Knopfhoehen flach statt fünf. Nachgemessen im Browser
                  (Prüfstand 041), nicht geschätzt. */}
              <th>Name</th>
              <th style={{ width: 190 }}>Mailadresse</th>
              <th style={{ width: 150 }}>Firma</th>
              <th style={{ width: 180 }}>Gruppen</th>
              <th className="zahl" style={{ width: 70 }}>Rechte</th>
              {/* ZWEI ZEITPUNKTE IN EINER SPALTE (.247). Marvin: „das sind ja
                  auch zwei verschiedene daten." Angemeldet ist, wer eine
                  Sitzung geöffnet hat; aktiv, wer darin zuletzt etwas getan
                  hat. Eine zweite Spalte nähme der Knopfleiste den Platz. */}
              <th style={{ width: 130 }}>Anmeldung / aktiv</th>
              <th style={{ width: 60 }}>2FA</th>
              <th style={{ width: 220 }} />
            </tr>
          </thead>
          <tbody>
            {/* EIN KLICK AUF DIE ZEILE ÖFFNET SIE - WIE ÜBERALL SONST.
                ORIGINALTEXT MARVIN (07.09.2026)
                  „der benutzer kann die menüpunkte immernoch nicht sehen,
                   auch kann ich sie ihm nicht zuweisen, weil in den
                   berechtigungen nichts hinzugekommen ist."

                ER HAT RECHT, UND DIE KARTE WAR DIE GANZE ZEIT DA. Sie geht
                nur auf, wenn man einen KLEINEN KNOPF am rechten Ende einer
                Leiste mit bis zu fünf anderen drückt; die Zeile selbst trug
                `cursor: default` und tat nichts. In der Arbeitsliste, in den
                Gemeinden, im Leistungskatalog öffnet ein Klick auf die Zeile
                den Datensatz - **hier war es der einzige Bildschirm, auf dem
                das nicht gilt.** Im Browser nachgemessen: Zeile angeklickt,
                nichts; Knopf angeklickt, 81 Kästchen und 21 Menümarken.

                **Ein Weg, den man nicht findet, ist so gut wie keiner** -
                dieselbe Lehre wie bei den Vordrucken (27.08.2026), die als
                siebter Reiter niemand gesucht hat.

                Der Knopf bleibt daneben: er sagt, was die Zeile tut, und auf
                dem Telefon trifft ein Daumen ihn genauer als eine Zeile. */}
            {benutzer.map((x: any) => (
              <Fragment key={x.id}>
              <tr style={{ cursor: 'pointer' }}
                  className={gewaehlt === x.id ? 'gewaehlt' : undefined}
                  onClick={() => waehle(gewaehlt === x.id ? null : x.id)}>
                <td>
                  <b>{x.name}</b>
                  {!x.aktiv && <span className="leise klein"> nicht aktiv</span>}
                </td>
                <td className="klein leise">{x.email}</td>
                <td className="klein">
                  {x.firma ?? (x.ne4_firma ? <>{x.ne4_firma} <span className="leise">, NE4</span></> : <span className="leise">Innendienst</span>)}
                </td>
                <td className="klein">
                  {liste(x.gruppen).map((n: string) => (
                    <span key={n} className="gewerk">{n}</span>
                  ))}
                  {x.ausnahmen > 0 && (
                    <span className="gewerk" title="Einzelrechte abweichend von den Gruppen">
                      +{x.ausnahmen} Ausnahme{x.ausnahmen === 1 ? '' : 'n'}
                    </span>
                  )}
                </td>
                <td className="zahl">{x.rechte}</td>
                <td className="klein leise">
                  {/* OHNE PASSWORT KOMMT NIEMAND MEHR HEREIN, seit der
                      Anmeldelink weg ist. Das muss man sehen, ohne danach zu
                      suchen: sonst merkt es der Betreffende am Montagmorgen. */}
                  {/* SEIT MIGRATION 054 SIND ES DREI ZUSTAENDE UND NICHT
                      ZWEI. „Kein Passwort, aber eine Einladung unterwegs" ist
                      etwas anderes als „kein Passwort und niemand kümmert
                      sich" - und wer den Unterschied nicht sieht, schickt
                      eine zweite Einladung, die die erste zurückzieht. */}
                  {x.hat_passwort
                    ? 'Passwort'
                    : x.einladung_bis
                      ? <b className="leise">Einladung läuft</b>
                      : <b className="rot">kein Passwort</b>}
                  {/* WER WARTET, STEHT AUF EINER LISTE. Ein Wartender, den
                      niemand sieht, wartet fuer immer - und das Datum daneben
                      ist der Unterschied zwischen „seit heute" und „seit drei
                      Wochen". */}
                  {x.wartet && (
                    <div className="merker warnung"
                         title="Dieser Zugang kommt herein, sieht aber nur die
                                Warteseite. Er kann sein Passwort und den
                                zweiten Faktor einrichten und sonst nichts.">
                      wartet{x.wartet_seit ? ` seit ${datum(x.wartet_seit)}` : ''}
                    </div>
                  )}
                  {x.passwort_wechseln && (
                    <div className="merker warnung"
                         title="Im Büro gesetzt. Beim nächsten Anmelden muss
                                er es ändern, bevor er etwas anderes tun kann.">
                      muss wechseln
                    </div>
                  )}
                  {/* AN DIESEM ZUGANG HÄNGEN SCHLÜSSEL.
                      Marvin, 06.09.2026: „wo sehe ich denn die schlüssel, die
                      ich der person geordnet habe?"

                      SIE GEHÖREN DORTHIN, WO MAN DEN ZUGANG ANFASST. Ein
                      Schlüssel kann nie mehr als sein Mensch, und ein
                      stillgelegter Zugang trägt gar keinen mehr - wer hier
                      ein Recht entzieht oder stilllegt, trifft sie mit, und
                      das stand bisher nur auf einem anderen Bildschirm.

                      DIE ZAHL, NICHT DIE NAMEN: die Liste steht unter
                      „Schnittstelle", je Zugang filterbar. Zwei Listen
                      derselben Sache laufen auseinander. */}
                  {x.schluessel > 0 && (
                    <div className="merker"
                         title={'Unter „Schnittstelle" stehen sie mit Kennung '
                              + 'und Stand, je Zugang filterbar.'}>
                      {x.schluessel} Schlüssel
                      {x.schluessel_gesperrt > 0
                        && ` (${x.schluessel_gesperrt} gesperrt)`}
                    </div>
                  )}
                  {x.letzte_anmeldung
                    ? <div><span className="leise">Letzte Anmeldung:</span> {zeitpunkt(x.letzte_anmeldung)}</div>
                    : <div className="leise">noch nie angemeldet</div>}
                  <div title={x.zuletzt_aktiv ? zeitpunkt(x.zuletzt_aktiv) : undefined}>
                    <span className="leise">Zuletzt aktiv:</span>{' '}
                    {x.zuletzt_aktiv ? wannAktiv(x.zuletzt_aktiv) : 'noch nie'}
                  </div>
                </td>
                <td className="klein">
                  {/* SEIT MIGRATION 042. „2FA", weil Marvin es so nennt.
                      Kein Rot, wenn keiner da ist: solange die Pflicht auf
                      „niemand" steht, ist das keine Lücke, sondern die
                      Einstellung. Rot wird es im Reiter „Sicherheit", wo
                      steht, was das Umlegen kostet. */}
                  {x.hat_faktor
                    ? <span className="marke-zustand z-erledigt">ja</span>
                    : <span className="leise">keine</span>}
                  {/* WARUM ER NICHT HEREINKOMMT (.276): der letzte
                      Fehlversuch mit Grund, solange danach keiner glückte. */}
                  {x.fehl_grund && (
                    <div className="rot" style={{ marginTop: 4 }} title={zeitpunkt(x.fehl_am)}>
                      {x.fehl_grund}
                      <div className="leise">{zeitpunkt(x.fehl_am)}</div>
                    </div>
                  )}
                </td>
                {/* EINE KNOPFLEISTE IST EINE LEISTE UND KEIN FLIESSTEXT.
                    Marvin, 27.08.2026, mit Bildschirmfoto: „hier ist die
                    buttons nicht sauber dargestellt."

                    Er hat recht, und die Ursache war banal: fünf Knöpfe
                    standen als INLINE-Elemente nebeneinander, jeder mit einem
                    eigenen `marginLeft: 6`. Sie brachen an beliebiger Stelle
                    um, jede Zeile fing woanders an, und weil der linke Rand
                    des ersten Knopfes fehlte, während alle anderen einen
                    hatten, standen die Zeilen versetzt zueinander. Bei drei
                    Benutzern mit verschiedenen Rechten ergab das drei
                    verschiedene Muster untereinander.

                    Jetzt ein Raster mit fester Spaltenbreite: was umbricht,
                    bricht in eine SPALTE um und nicht an eine zufällige
                    Stelle, und jeder Knopf ist gleich breit. Der Abstand
                    steht EINMAL im `gap` und nicht fünfmal als `marginLeft`. */}
                {/* DIE KNOPFZELLE GIBT DEN KLICK NICHT WEITER. Sonst legt
                    „Login als" gleichzeitig die Karte um, und „zuklappen"
                    klappt zweimal - also gar nicht. */}
                <td className="knopfleiste" onClick={(e) => e.stopPropagation()}>
                  <button className="knopf klein"
                          title="Gruppen, Menü und die Rechte dieses Zugangs"
                          onClick={() => waehle(gewaehlt === x.id ? null : x.id)}>
                    {gewaehlt === x.id ? 'zuklappen' : 'Rechte'}
                  </button>
                  {kannUebernehmen && x.id !== ich.benutzerId && x.aktiv && (
                    <button className="knopf klein"
                            disabled={arbeitet}
                            onClick={() => hinein(x.id, x.name)}>
                      Login als
                    </button>
                  )}
                  {kannPasswort && x.aktiv && (
                    <button
                      className={'knopf klein' + (x.hat_passwort ? '' : ' haupt')}
                      disabled={arbeitet}
                      title={x.hat_passwort
                        ? 'Ein neues Passwort setzen. Alle Sitzungen enden dabei.'
                        : 'Ohne Passwort kommt dieser Benutzer nicht herein.'}
                      onClick={() => setPw({ id: x.id, name: x.name })}>
                      Passwort
                    </button>
                  )}
                  {/* EINE EINLADUNG NACHSCHICKEN. Sie zieht die vorige
                      zurück: sonst hat jemand zwei gültige Wege, und der
                      ältere liegt vergessen im Postfach. */}
                  {kannPasswort && x.aktiv && (
                    <button
                      className={'knopf klein'
                        + (!x.hat_passwort && !x.einladung_bis ? ' haupt' : '')}
                      disabled={arbeitet}
                      title={x.einladung_bis
                        ? 'Es läuft schon eine Einladung. Eine neue zieht die '
                          + 'alte zurück.'
                        : 'Eine Mail mit einem Link, über den er sein Passwort '
                          + 'selbst setzt. Der Link meldet nicht an.'}
                      onClick={() => lade(x)}>
                      Einladung
                    </button>
                  )}
                  {/* NAME, MAIL, TELEFON, TELEGRAM, FIRMA, AKTIV.
                      Nichts davon liess sich bis zum 27.08.2026 in der
                      Oberfläche ändern - es gab nicht einmal einen Weg im
                      Server dafür. */}
                  {/* FREISCHALTEN ODER AUF DIE WARTESEITE SETZEN. Ein eigener
                      Knopf und kein Feld im Aenderungsfenster: beim
                      Freischalten geht eine Mail hinaus, und eine Nebenwirkung
                      an einem beliebigen Feldwechsel ueberrascht beim
                      zwanzigsten Mal. Am eigenen Zugang steht er nicht: wer
                      sich selbst auf die Warteseite setzt, kaeme an diesen
                      Knopf nicht mehr heran. */}
                  {kannAnlegen && x.aktiv && x.id !== ich.benutzerId && (
                    <button
                      className={'knopf klein' + (x.wartet ? ' haupt' : '')}
                      disabled={arbeitet}
                      title={x.wartet
                        ? 'Freischalten: er sieht ab sofort seine Aufträge, '
                          + 'und eine Mail geht an ihn hinaus.'
                        : 'Auf die Warteseite setzen: er kommt weiter herein, '
                          + 'sieht aber nichts außer der Willkommensseite.'}
                      onClick={() => freischaltung(x, !x.wartet)}>
                      {x.wartet ? 'freischalten' : 'Warteseite'}
                    </button>
                  )}
                  {kannAnlegen && (
                    <button className="knopf klein"
                            disabled={arbeitet}
                            onClick={() => setZugang(x)}>
                      ändern
                    </button>
                  )}
                  {/* 2FA ZURÜCKSETZEN (.276): für den, der nicht mehr
                      hereinkommt. Er meldet sich danach mit Passwort an und
                      scannt einen neuen QR-Code. */}
                  {darf(ich, 'benutzer.verwalten') && x.hat_faktor && x.id !== ich.benutzerId && (
                    <button className={'knopf klein' + (x.fehl_grund ? ' haupt' : '')}
                            disabled={arbeitet}
                            title="Die Authenticator-App dieses Zugangs abmelden. Bei der nächsten Anmeldung mit Passwort richtet er sie neu ein."
                            onClick={() => faktorZurueck(x)}>
                      2FA Reset
                    </button>
                  )}
                </td>
              </tr>
              {/* .576: DIE RECHTE STEHEN DIREKT UNTER SEINER ZEILE. Marvin,
                  02.11.2026: „wenn ich das ausklappe ... muss das direkt
                  darunter erscheinen und nicht erst nach unten scrollen
                  müssen." Bis hier stand die Karte unter der ganzen Liste. */}
              {gewaehlt === x.id && d && d.id === x.id && (
                <tr className="detailzeile">
                  <td colSpan={8} data-voll="" data-spalte="">
        <Karte titel={`${d.name}: was gilt für ihn`}>
          <p className="klein leise" style={{ marginTop: 0 }}>
            {d.firma
              ? `Gehört zu ${d.firma} und sieht deshalb nur Aufträge, an denen `
                + 'diese Firma ein Gewerk hat. Diese Grenze hängt an der Firma, '
                + 'nicht an einer Gruppe, und lässt sich hier nicht abschalten.'
              : 'Gehört zu keiner Firma und sieht deshalb alle Aufträge.'}
          </p>
          {/* .576: DIE NE4-ZUORDNUNG. Die Rechte kommen aus den Gruppen hier,
              die Reichweite in NE4 aus „NE4, Firmen und Teams". */}
          {(() => {
            const ng = (d.gruppen ?? []).map((g: any) => g.schluessel);
            const team = d.ne4?.teamzugang, firma = d.ne4?.firmenzugang;
            if (!team && !firma && !ng.some((g: string) => g === 'ne4_team' || g === 'ne4_firma')) return null;
            return (
              <p className={'klein' + ((ng.includes('ne4_team') && !team) || (ng.includes('ne4_firma') && !firma) ? ' rot' : ' leise')} style={{ marginTop: 0 }}>
                <b>NE4:</b>{' '}
                {team ? `Teamzugang von ${team.kurz} ${team.team} (${team.firma}); sieht nur die Termine dieses Teams. `
                  : firma ? `Firmenzugang von ${firma.firma}; sieht Termine und Zahlen der Teams dieser Firma. ` : ''}
                {ng.includes('ne4_team') && !team && 'In der Gruppe NE4 Teamzugang, aber keinem Team zugeordnet: sieht in NE4 leere Listen. Zuordnen unter NE4, Firmen und Teams. '}
                {ng.includes('ne4_firma') && !firma && 'In der Gruppe NE4 Firmenzugang, aber keiner Firma zugeordnet: sieht in NE4 leere Listen. Zuordnen unter NE4, Firmen und Teams, „Zugänge". '}
              </p>
            );
          })()}

          <h4 style={{ marginBottom: 6 }}>Gruppen</h4>
          <div className="reihe">
            {gruppen.map((g: any) => {
              const drin = d.gruppen.some((x: any) => x.id === g.id);
              return (
                <label key={g.id} className="gewerk" style={{ cursor: 'pointer' }}
                       title={g.beschreibung ?? ''}>
                  <input
                    type="checkbox" checked={drin} disabled={arbeitet}
                    onChange={(e) =>
                      gruppenSetzen(e.target.checked
                        ? [...d.gruppen.map((x: any) => x.id), g.id]
                        : d.gruppen.filter((x: any) => x.id !== g.id)
                                   .map((x: any) => x.id))}
                  />{' '}
                  {g.name}
                </label>
              );
            })}
          </div>

          {/* WAS ER IM MENÜ SIEHT - UND WARUM EIN PUNKT FEHLT.
              Marvin, 07.09.2026: „ihm fehlen wichtige punkte zum
              innendienst." Die Frage war bis hierher nur mit „Login als" zu
              beantworten. Die beiden Gründe haben zwei verschiedene nächste
              Schritte, und deshalb stehen sie getrennt da. */}
          {d.menue && (
            <>
              <h4 style={{ marginTop: 16, marginBottom: 6 }}>
                Menü ({d.menue.filter((m: any) =>
                  m.im_menue && m.menuestand === 'steht').length} Punkte)
              </h4>
              {/* EINE MARKE, DIE EINEN MANGEL NENNT, IST EIN WEG DORTHIN.
                  Marvin, 07.09.2026: „auch kann ich sie ihm nicht zuweisen."
                  Die Marke sagte, WARUM ein Punkt fehlt, und liess ihn
                  danach suchen: das Raster darunter hat 24 Zeilen. Ein Klick
                  springt jetzt zu dem Modul, um das es geht, und hebt es
                  kurz hervor. **Eine Liste von Mängeln ohne den Weg zur
                  Erledigung ist eine Liste von Vorwürfen** - dieselbe Lehre
                  wie beim Chip in „Was noch fehlt" (01.09.2026).

                  ER SETZT KEIN RECHT. Welches Recht ein Bildschirm braucht,
                  entscheidet der Mensch davor - ein Sprung ist eine Hilfe,
                  ein gesetzter Haken wäre eine geratene Entscheidung. */}
              <div className="hakenreihe">
                {d.menue.filter((m: any) => m.im_menue).map((m: any) => (
                  <button key={m.schluessel} type="button"
                        className={'menuemarke ' + m.menuestand}
                        title={{
                          steht: 'Steht in seiner Menüleiste. Anklicken: zu '
                            + 'den Rechten dieses Bildschirms.',
                          kein_recht: 'Keine seiner Gruppen trägt ein Recht '
                            + 'auf diesen Bildschirm. Zu beheben unter '
                            + '„Rechte ändern" - oder hier, als Ausnahme. '
                            + 'Anklicken: zu den Rechten dieses Bildschirms.',
                          ausgeblendet: 'Er hat das Recht, aber jede seiner '
                            + 'Gruppen hat den Punkt ausgeblendet. Zu beheben '
                            + 'unter „Gruppen → Menü ändern".',
                        }[m.menuestand as string]}
                        onClick={() => {
                          const z = document.getElementById(
                            'zugangsrecht-' + m.schluessel);
                          if (!z) return;
                          z.scrollIntoView({ behavior: 'smooth', block: 'center' });
                          z.classList.add('hebt');
                          setTimeout(() => z.classList.remove('hebt'), 2000);
                        }}>
                    <i className="punkt" /> {m.name}
                  </button>
                ))}
              </div>
              <p className="klein leise" style={{ marginTop: 6 }}>
                <b>grün</b> steht in seiner Leiste, <b>grau</b> kein Recht
                darauf, <b>orange</b> vorhanden, aber von seinen Gruppen
                ausgeblendet.
              </p>
            </>
          )}

          <h4 style={{ marginTop: 16, marginBottom: 6 }}>
            Wirksame Rechte ({wirksamJetzt.size})
          </h4>
          {/* HIER STAND EIN RASTER OHNE `aendern`, ALSO EINES, IN DEM JEDES
              KÄSTCHEN GESPERRT WAR. Ein Klick setzt jetzt eine AUSNAHME für
              diesen einen Menschen; was die Gruppe trägt, ändert sich davon
              nicht. Der Riegel bleibt im Server: er verlangt zu jeder
              Ausnahme einen Grund und weist ohne ihn ab. */}
          <p className="klein leise" style={{ marginTop: 0 }}>
            Ein Klick setzt eine <b>Ausnahme für diesen Zugang</b> - er gibt
            ein Recht dazu oder nimmt eines weg, das seine Gruppe trägt. Wer
            eine ganze Gruppe ändern will, tut das unter „Gruppen".
            <b> Entzug gewinnt</b>, und ein zweiter Klick nimmt die Ausnahme
            wieder zurück.
          </p>
          <RechteRaster
            module={module}
            gesetzt={wirksamJetzt}
            aendern={(recht: string) => rechtSchalten(recht)}
            modulSchalten={modulSchalten}
            idVorsatz="zugangsrecht-"
            herkunft={Object.fromEntries(
              d.wirksam.map((w: any) => [w.recht_schluessel, w.herkunft]))}
            ausnahmen={ausnahmenJetzt.map((a) => ({
              recht_schluessel: a.recht, erlaubt: a.erlaubt }))}
          />

          {rechteEntwurf && (
            <div className="reihe" style={{ marginTop: 12, alignItems: 'flex-end' }}>
              <label className="wachsen">Grund für die Ausnahmen
                <input value={rechteGrund} disabled={arbeitet}
                       placeholder="Vertritt den Innendienst bis Ende Oktober"
                       onChange={(e) => setRechteGrund(e.target.value)} />
              </label>
              <button className="knopf haupt" disabled={arbeitet || grundFehlt}
                      title={grundFehlt
                        ? 'Ohne Grund weiß in 3 Monaten niemand mehr, ob die '
                          + 'Ausnahme noch gilt. Der Server weist sie ab.'
                        : undefined}
                      onClick={rechteSpeichern}>
                {arbeitet ? 'Einen Moment'
                          : `${ausnahmenJetzt.length} Ausnahmen speichern`}
              </button>
              <button className="knopf" disabled={arbeitet}
                      onClick={() => { setRechteEntwurf(null); setRechteGrund(''); }}>
                verwerfen
              </button>
            </div>
          )}
          {rechteEntwurf && grundFehlt && (
            <p className="klein warnwort" style={{ marginTop: 4 }}>
              Es fehlt der Grund.
            </p>
          )}

          {d.ausnahmen.length > 0 && (
            <>
              <h4 style={{ marginTop: 16, marginBottom: 6 }}>Ausnahmen</h4>
              <table className="liste">
                <thead>
                  <tr><th style={{ width: 220 }}>Recht</th>
                  <th style={{ width: 110 }}>Wirkung</th><th>Grund</th>
                  <th style={{ width: 180 }}>gesetzt von</th></tr>
                </thead>
                <tbody>
                  {d.ausnahmen.map((a: any) => (
                    <tr key={a.recht_schluessel} style={{ cursor: 'default' }}>
                      <td className="klein"><code>{a.recht_schluessel}</code></td>
                      <td className="klein">
                        {a.erlaubt ? 'zusätzlich' : 'entzogen'}
                      </td>
                      <td className="klein leise">{a.grund}</td>
                      <td className="klein leise">
                        {a.gesetzt_von} {zeitpunkt(a.gesetzt_am)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </Karte>
                  </td>
                </tr>
              )}
              </Fragment>
            ))}
          </tbody>
        </table>
      </Karte>

    </>
  );
}


// ----------------------------------------------------------------- Gruppen

function GruppenListe({ gruppen, module, neuLaden }: any) {
  const [offen, setOffen] = useState<string | null>(null);
  const [entwurf, setEntwurf] = useState<Set<string> | null>(null);
  /* ZWEI KARTEN, ZWEI ENTWÜRFE. „Rechte ändern" und „Menü ändern" sind zwei
     Fragen: was jemand DARF und was er in der Leiste SIEHT. Ein gemeinsamer
     Entwurf hiesse, dass ein Klick im Menü einen ungespeicherten Rechtestand
     verwirft, ohne dass es jemand merkt. */
  const [menue, setMenue] = useState<string | null>(null);
  const [menueEntwurf, setMenueEntwurf] = useState<Set<string> | null>(null);
  const [arbeitet, setArbeitet] = useState(false);
  const [meldung, setMeldung] = useState<string | null>(null);
  const [neu, setNeu] = useState(false);
  const [neuName, setNeuName] = useState('');
  const [neuKuerzel, setNeuKuerzel] = useState('');
  /* ZU WELCHEM BEREICH DIE NEUE GRUPPE GEHÖRT (.333). Marvin, 08.10.2026:
     „Die gemeinsamen Bereiche, Schema Public, sind natürlich nur für die
     Leute sichtbar oder für die Person, die die Rechte hat, primär ich als
     Administrator." Vorgabe NE3; „alle" ist eine ausdrückliche Wahl. */
  const [neuBereich, setNeuBereich] = useState('ne3');
  const [bereiche, setBereiche] = useState<any[]>([]);
  useEffect(() => { api.bereiche().then(setBereiche).catch(() => setBereiche([])); }, []);
  const bereichName = (b: string | null) => b ? (bereiche.find((x) => x.schluessel === b)?.kurz ?? b.toUpperCase()) : 'alle Bereiche';
  /* DAS RASTER ZEIGT NUR DIE MODULE DES BEREICHS DER GRUPPE: eine NE4-Gruppe
     bekommt keine Haken für Behördenpost, eine NE3-Gruppe keine für NE4-Teams.
     Die gemeinsamen Bildschirme stehen in beiden; eine Gruppe ohne Bereich
     sieht alles. */
  const moduleFuer = (g: any) => (module ?? []).filter((m: any) => !g?.bereich || !m.bereiche || m.bereiche.includes(g.bereich));

  function oeffne(g: any) {
    if (offen === g.id) { setOffen(null); setEntwurf(null); return; }
    setOffen(g.id); setEntwurf(new Set(liste(g.rechte)));
    setMenue(null); setMenueEntwurf(null);
    setMeldung(null);
  }

  /* DIE MASKE HAKT AN, WAS ERSCHEINEN SOLL. In der Datenbank steht die
     Ausnahme („nicht anzeigen"), hier steht die Liste. Wer die Umdrehung in
     der Maske UND im Server hätte, hätte sie zweimal. */
  function oeffneMenue(g: any) {
    if (menue === g.id) { setMenue(null); setMenueEntwurf(null); return; }
    const aus = new Set(liste(g.menue_aus));
    setMenue(g.id);
    setMenueEntwurf(new Set(
      (module ?? []).map((m: any) => m.schluessel)
                    .filter((s: string) => !aus.has(s))));
    setOffen(null); setEntwurf(null);
    setMeldung(null);
  }

  async function menueSpeichern(g: any) {
    setArbeitet(true); setMeldung(null);
    try {
      await api.gruppeMenue(g.id, [...menueEntwurf!]);
      await neuLaden();
      setMenue(null); setMenueEntwurf(null);
    } catch (f: any) { setMeldung(f.message); } finally { setArbeitet(false); }
  }

  async function speichern(g: any) {
    setArbeitet(true); setMeldung(null);
    try {
      await api.gruppeRechte(g.id, [...entwurf!]);
      await neuLaden();
      setOffen(null); setEntwurf(null);
    } catch (f: any) { setMeldung(f.message); } finally { setArbeitet(false); }
  }

  async function anlegen() {
    setArbeitet(true); setMeldung(null);
    try {
      await api.gruppeAnlegen({ schluessel: neuKuerzel.trim(), name: neuName.trim(),
                                bereich: neuBereich === 'alle' ? null : neuBereich });
      await neuLaden();
      setNeu(false); setNeuName(''); setNeuKuerzel(''); setNeuBereich('ne3');
    } catch (f: any) { setMeldung(f.message); } finally { setArbeitet(false); }
  }

  async function loeschen(g: any) {
    if (!await fragen(`Gruppe "${g.name}" löschen?`)) return;
    setArbeitet(true); setMeldung(null);
    try { await api.gruppeLoeschen(g.id); await neuLaden(); }
    catch (f: any) { setMeldung(f.message); } finally { setArbeitet(false); }
  }

  return (
    <>
      {meldung && <div className="hinweis fehler">{meldung}</div>}

      <Karte titel={`Gruppen (${gruppen.length})`}>
        <p className="klein leise" style={{ marginTop: 0 }}>
          Die Oberfläche fragt immer das Recht, nie die Gruppe. Eine neue Gruppe
          anzulegen ändert deshalb keine einzige Abfrage.
        </p>

        <table className="liste">
          <thead>
            <tr><th style={{ width: 220 }}>Gruppe</th><th>Wofür</th>
            <th style={{ width: 110 }}>Bereich</th>
            <th className="zahl" style={{ width: 90 }}>Rechte</th>
            <th className="zahl" style={{ width: 90 }}>Benutzer</th>
            <th style={{ width: 240 }} /></tr>
          </thead>
          <tbody>
            {gruppen.map((g: any) => (
              <tr key={g.id} style={{ cursor: 'default' }}>
                <td>
                  <b>{g.name}</b>
                  <div className="klein leise"><code>{g.schluessel}</code>
                    {g.system && ', System'}</div>
                </td>
                <td className="klein leise">{g.beschreibung ?? ''}</td>
                <td className="klein">{bereichName(g.bereich)}</td>
                <td className="zahl">{liste(g.rechte).length}</td>
                <td className="zahl">{g.benutzer}</td>
                {/* DIESELBE LEISTE WIE OBEN, aus demselben Grund: zwei Knöpfe
                    nebeneinander, der zweite mit einem eigenen Rand am linken
                    Rand. Sobald „Rechte ändern" und „löschen" nicht mehr in
                    eine Zeile passen, sass „löschen" um sechs Pixel versetzt
                    unter dem anderen. */}
                <td className="knopfleiste breit">
                  <button className="knopf klein" onClick={() => oeffne(g)}>
                    {offen === g.id ? 'zuklappen' : 'Rechte ändern'}
                  </button>
                  {/* Marvin, 01.09.2026: „baue es so, dass ich es für die
                      gruppe innendienst selbst selektieren kann, was sie
                      sehen von allen punkten." */}
                  <button className="knopf klein" onClick={() => oeffneMenue(g)}>
                    {menue === g.id ? 'zuklappen' : 'Menü ändern'}
                  </button>
                  {!g.system && (
                    <button className="knopf klein"
                            disabled={arbeitet} onClick={() => loeschen(g)}>
                      löschen
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {neu ? (
          <div className="reihe" style={{ marginTop: 12, alignItems: 'flex-end' }}>
            <label>Name
              <input value={neuName} onChange={(e) => setNeuName(e.target.value)}
                     placeholder="Bauleitung extern" style={{ width: 220 }} />
            </label>
            <label>Kürzel
              <input value={neuKuerzel} placeholder="bauleitung_extern"
                     onChange={(e) => setNeuKuerzel(e.target.value)}
                     style={{ width: 200 }} />
            </label>
            <label>Bereich
              <select value={neuBereich} onChange={(e) => setNeuBereich(e.target.value)} style={{ width: 200 }}>
                {bereiche.map((b) => <option key={b.schluessel} value={b.schluessel}>{b.kurz}, {b.untertitel}</option>)}
                <option value="alle">alle Bereiche</option>
              </select>
            </label>
            <button className="knopf haupt" disabled={arbeitet || !neuName || !neuKuerzel}
                    onClick={anlegen}>anlegen</button>
            <button className="knopf" onClick={() => setNeu(false)}>abbrechen</button>
          </div>
        ) : (
          <button className="knopf" style={{ marginTop: 12 }}
                  onClick={() => setNeu(true)}>Neue Gruppe</button>
        )}
      </Karte>

      {offen && entwurf && (
        <Karte titel={`Rechte von "${gruppen.find((g: any) => g.id === offen).name}"`}>
          <RechteRaster
            module={moduleFuer(gruppen.find((g: any) => g.id === offen))} gesetzt={entwurf}
            extern={gruppen.find((g: any) => g.id === offen)?.extern}
            aendern={(recht: string, an: boolean) => {
              const n = new Set(entwurf);
              an ? n.add(recht) : n.delete(recht);
              setEntwurf(n);
            }}
            modulSchalten={(rechte: string[], an: boolean) => {
              const n = new Set(entwurf);
              rechte.forEach((r) => (an ? n.add(r) : n.delete(r)));
              setEntwurf(n);
            }}
          />
          <div className="reihe" style={{ marginTop: 14 }}>
            <button className="knopf haupt" disabled={arbeitet}
                    onClick={() => speichern(gruppen.find((g: any) => g.id === offen))}>
              {arbeitet ? 'Einen Moment' : `${entwurf.size} Rechte speichern`}
            </button>
            <button className="knopf" onClick={() => { setOffen(null); setEntwurf(null); }}>
              verwerfen
            </button>
          </div>
        </Karte>
      )}

      {menue && menueEntwurf && (
        <Karte titel={'Menü von "'
                      + gruppen.find((g: any) => g.id === menue).name + '"'}>
          {/* WAS DIESER HAKEN TUT UND WAS NICHT - und der zweite Satz ist der
              wichtigere. Ein Bildschirm, der so täte, als nähme er etwas weg,
              wäre die teurere Sorte Fehler: man verlässt sich darauf. */}
          <p className="klein leise" style={{ marginTop: 0 }}>
            Der Haken sagt, ob der Punkt in der Menüleiste erscheint.
            <b> Er blendet aus, er nimmt nichts weg:</b> wer die Adresse kennt,
            kommt weiter auf den Bildschirm, und die Daten dahinter kommen
            weiter heraus. Wer etwas WEGNEHMEN will, nimmt das Recht - eine
            Zeile darüber, unter „Rechte ändern".
          </p>
          <p className="klein leise">
            Ein Punkt, auf den diese Gruppe gar kein Recht hat, steht ohnehin
            nicht in der Leiste. Der Haken hier hält ihn also nur dann
            heraus, wenn sie ihn sonst sähe.
          </p>

          <MenueRaster
            module={moduleFuer(gruppen.find((g: any) => g.id === menue))}
            gruppe={gruppen.find((g: any) => g.id === menue)}
            gesetzt={menueEntwurf}
            aendern={(schluessel: string, an: boolean) => {
              const n = new Set(menueEntwurf);
              an ? n.add(schluessel) : n.delete(schluessel);
              setMenueEntwurf(n);
            }}
          />
          <div className="reihe" style={{ marginTop: 14 }}>
            <button className="knopf haupt" disabled={arbeitet}
                    onClick={() =>
                      menueSpeichern(gruppen.find((g: any) => g.id === menue))}>
              {arbeitet ? 'Einen Moment'
                        : `${menueEntwurf.size} Menüpunkte speichern`}
            </button>
            <button className="knopf"
                    onClick={() => { setMenue(null); setMenueEntwurf(null); }}>
              verwerfen
            </button>
          </div>
        </Karte>
      )}
    </>
  );
}


/**
 * Welche Menüpunkte eine Gruppe sieht.
 *
 * ORIGINALTEXT MARVIN (01.09.2026)
 *   „der innendienst muss nicht alle möglichen menüpunkte sehen. baue es so,
 *    dass ich es für die gruppe innendienst selbst selektieren kann, was sie
 *    sehen von allen punkten."
 *
 * GEGLIEDERT NACH MENÜGRUPPEN, in genau der Reihenfolge, in der die Leiste
 * sie zeigt. Eine alphabetische Liste aller Module wäre dieselbe Auskunft in
 * einer Ordnung, die auf keinem Bildschirm vorkommt - und dann sucht man
 * „Kapazität" unter K statt unter „Planung", wo es steht.
 *
 * UND EIN PUNKT OHNE RECHT STEHT GRAU DA. Ihn wegzulassen wäre die
 * naheliegende Antwort und die falsche: dann fehlt in der Liste, was die
 * Gruppe gerade nicht hat, und wer ein Recht erteilt, sucht den Haken dazu
 * vergeblich. Er steht da und sagt, warum er nichts bewirkt.
 */
function MenueRaster({ module, gruppe, gesetzt, aendern }: {
  module: any[];
  gruppe: any;
  gesetzt: Set<string>;
  aendern: (schluessel: string, an: boolean) => void;
}) {
  const rechte = new Set<string>(liste(gruppe?.rechte));
  const hatRecht = (m: any) =>
    (m.rechte ?? []).some((r: any) => rechte.has(r.schluessel ?? r));
  const menuegruppen = [...new Set(module.map((m: any) => m.menuegruppe))];

  return (
    <div>
      {menuegruppen.map((mg) => (
        <div key={mg} style={{ marginBottom: 10 }}>
          <div className="klein leise" style={{ fontWeight: 700 }}>{mg}</div>
          <div className="hakenreihe">
            {module.filter((m: any) => m.menuegruppe === mg).map((m: any) => (
              <label key={m.schluessel}
                     className={'wahlzeile' + (hatRecht(m) ? '' : ' leise')}
                     title={hatRecht(m) ? undefined
                       : 'Diese Gruppe hat auf diesen Bildschirm kein Recht - '
                         + 'er steht ohnehin nicht in ihrer Leiste.'}>
                <input type="checkbox" checked={gesetzt.has(m.schluessel)}
                       onChange={(e) => aendern(m.schluessel, e.target.checked)} />
                <span>{m.name}</span>
              </label>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}


// ------------------------------------------------------ Das Raster selbst

/**
 * Module in Zeilen, Funktionen als Haken. Das ist die Ansicht, in der
 * "Zugriff auf ein Modul geben" und "eine einzelne Funktion geben" dieselbe
 * Handbewegung sind.
 */
function RechteRaster({
  module, gesetzt, aendern, modulSchalten, herkunft, ausnahmen, idVorsatz,
  extern,
}: {
  module: any[];
  gesetzt: Set<string>;
  /* DIESE GRUPPE GEHÖRT ZU EINER PARTNERFIRMA.
     ====================================================================
     ORIGINALTEXT MARVIN (22.09.2026)
       „Also arbeiten mit dem Leser darf nur der Innendienst und ich. Sonst
        keiner."

     Der Riegel steht in der Datenbank und im Weg dahinter. Hier wird der
     Haken gesperrt, damit niemand auf eine rote Meldung läuft: **ein Knopf,
     der auf eine rote Meldung führt, ist schlimmer als kein Knopf** - man
     drückt ihn und glaubt der Oberfläche danach nicht mehr. */
  extern?: boolean;
  aendern?: (recht: string, an: boolean) => void;
  modulSchalten?: (rechte: string[], an: boolean) => void;
  herkunft?: Record<string, string>;
  ausnahmen?: any[];
  /* WOZU EIN VORSATZ. Dieses Raster steht auf demselben Bildschirm zweimal -
     einmal für eine Gruppe, einmal für einen Zugang. Zwei Elemente mit
     derselben `id` sind im Dokument nicht erlaubt, und `getElementById`
     fände dann irgendeines von beiden. Ohne Vorsatz bekommt niemand eine. */
  idVorsatz?: string;
}) {
  const nurLesen = !aendern;
  const entzogen = new Set(
    (ausnahmen ?? []).filter((a) => !a.erlaubt).map((a) => a.recht_schluessel));

  const gruppen = [...new Set(module.map((m) => m.menuegruppe))];

  return (
    <div style={{ overflowX: 'auto' }}>
      {gruppen.map((mg) => (
        <div key={mg}>
          <h4 style={{ marginTop: 14, marginBottom: 4 }}>{mg}</h4>
          <table className="liste rechtetabelle">
            <tbody>
              {module.filter((m) => m.menuegruppe === mg).map((m) => {
                const alle = m.rechte.map((r: any) => r.schluessel);
                const wieviele = alle.filter((r: string) => gesetzt.has(r)).length;
                return (
                  <tr key={m.schluessel} style={{ cursor: 'default' }}
                      id={idVorsatz ? idVorsatz + m.schluessel : undefined}>
                    <td className="rt-name">
                      <b>{m.name}</b>
                      {/* HIER STAND DER BAUSTAND DES MODULS („fertig",
                          „nur lesend", „noch nicht gebaut"). Er ist mit
                          Migration 102 gefallen: seit dem 28.08.2026 stand
                          bei allen zweiundzwanzig dasselbe Wort. EINE ZEILE,
                          DIE IMMER DASSELBE SAGT, WIRD NICHT GELESEN - und
                          an dem Tag, an dem sie etwas anderes sagt, auch
                          nicht. Was hier zählt, ist die Reichweite. */}
                      {m.fuer_subs && (
                        <div className="klein leise">
                          auch für Partnerfirmen
                        </div>
                      )}
                    </td>
                    <td className="rt-alle">
                      {modulSchalten ? (
                        <button
                          className="knopf klein"
                          onClick={() => modulSchalten(alle, wieviele < alle.length)}
                        >
                          {wieviele === alle.length ? 'alle ab' : 'alle an'}
                        </button>
                      ) : (
                        <span className="klein leise">{wieviele} von {alle.length}</span>
                      )}
                    </td>
                    <td>
                      {/* EIN RASTER UND KEINE WOLKE.
                          Marvin, 01.09.2026: „das wirkt so unsortiert."
                          Hier stand `.reihe` - die Haken lagen nebeneinander,
                          jeder so breit wie sein Wort, und brachen dort um,
                          wo die Zelle zu Ende war. Zwölf verschieden breite
                          Kästchen über zwei Zeilen ergeben eine Wolke.
                          Gleich breite Zellen stellen die Kanten
                          untereinander - dieselbe Lehre wie bei der
                          Knopfleiste, und aus demselben Grund `auto-fill`
                          und nicht `auto-fit`: `auto-fit` legt leere Spalten
                          zusammen, und dann bekommt die Zeile mit drei Haken
                          andere Breiten als die mit zwölf. */}
                      <div className="rechteraster">
                        {m.rechte.map((r: any) => {
                          const an = gesetzt.has(r.schluessel);
                          const weg = entzogen.has(r.schluessel);
                          /* NICHT NACH DRAUSSEN. Das Recht steht weiter da,
                             damit sichtbar bleibt, dass es es gibt - es ist
                             nur gesperrt, und der Grund steht im Titel. */
                          const zu = !!extern && !!r.nur_innen;
                          return (
                            <label
                              key={r.schluessel}
                              className={'rechtehaken'
                                + (an ? ' an' : '') + (weg ? ' entzogen' : '')}
                              style={{ cursor: (nurLesen || zu)
                                ? 'default' : 'pointer',
                                opacity: zu ? 0.45 : undefined }}
                              title={
                                (zu
                                  ? 'Dieses Recht geht an keine Partnerfirma.\n'
                                  : '')
                                + (r.zweck ?? '')
                                + (herkunft?.[r.schluessel]
                                    ? `\naus: ${herkunft[r.schluessel]}` : '')
                                + (weg ? '\neinzeln entzogen' : '')
                              }
                            >
                              <input
                                type="checkbox" checked={an && !zu}
                                disabled={nurLesen || zu}
                                onChange={(e) =>
                                  aendern?.(r.schluessel, e.target.checked)}
                              />
                              <span className="wachsen">{r.funktion}</span>
                              {/* DAS AUSRUFEZEICHEN TRÄGT FARBE. Es sagt
                                  „dieses Recht greift tief ein", und in
                                  derselben Graustufe wie der Rest sagt es
                                  das niemandem. */}
                              {r.gefaehrlich && (
                                <span className="rechtehaken-warnung"
                                      title="Wirkt nach außen oder kostet Geld">
                                  !
                                </span>
                              )}
                            </label>
                          );
                        })}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
}


// ------------------------------------------------------- Module (nur lesen)

function ModulListe({ module, gruppen }: any) {
  return (
    <Karte titel={`Module und Funktionen (${module.length})`}>
      <p className="klein leise" style={{ marginTop: 0 }}>
        Welche Funktionen es gibt, entscheidet der Bau, nicht die Verwaltung.
        Deshalb ist diese Liste nur zum Lesen: ein Recht, hinter dem kein
        Programmcode steht, wäre ein Versprechen ohne Deckung. Die Spalte
        rechts zeigt, welche Gruppen die Funktion tragen.
      </p>
      <table className="liste">
        <thead>
          <tr><th style={{ width: 200 }}>Modul</th>
          <th style={{ width: 170 }}>Funktion</th><th>Zweck</th>
          <th style={{ width: 260 }}>Gruppen</th></tr>
        </thead>
        <tbody>
          {module.flatMap((m: any) =>
            m.rechte.map((r: any, i: number) => (
              <tr key={r.schluessel} style={{ cursor: 'default' }}>
                <td>
                  {i === 0 && (
                    <>
                      <b>{m.name}</b>
                      <div className="klein leise">{m.menuegruppe}</div>
                    </>
                  )}
                </td>
                <td className="klein">
                  {r.funktion}
                  {r.gefaehrlich && <span title="Wirkt nach außen"> !</span>}
                  <div className="leise"><code>{r.schluessel}</code></div>
                </td>
                <td className="klein leise">{r.zweck}</td>
                <td className="klein">
                  {gruppen
                    .filter((g: any) => liste(g.rechte).includes(r.schluessel))
                    .map((g: any) => (
                      <span key={g.id} className="gewerk">{g.name}</span>
                    ))}
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </Karte>
  );
}


// ------------------------------------------------- Wer ist wie abgesichert

/**
 * Die 2FA-Pflicht, je Gruppe, mit ihrer Wirkung daneben.
 *
 * ORIGINALTEXT MARVIN (26.08.2026)
 *   „,der zweite Faktor' heißt ,2FA' und muss noch korrekt eingebunden
 *    werden."
 *
 * ORIGINALTEXT MARVIN (03.09.2026)
 *   „2FA auch einzeln für die gruppen aktivierbar, merhfachauswahl zulässig"
 *
 * DER SCHALTER STEHT DA, WO MAN SEINE WIRKUNG SIEHT. Er liegt technisch in
 * der Konfiguration und lässt sich auch dort umlegen; nur steht dort neben
 * einem Gruppennamen keine Zeile darüber, dass er sieben Leute aussperrt.
 * Hier steht sie, mit Namen.
 *
 * BIS .92 WAREN ES DREI STUFEN, und die mittlere hiess nicht, was sie tat:
 * „Innendienst" traf JEDEN ohne Partnerfirma, also auch die Buchhaltung und
 * die Nurlesenden. Eine Aufschrift, die etwas anderes verspricht als der
 * Riegel dahinter tut, ist schlimmer als keine.
 *
 * DIE VORSCHAU LEGT DIE LISTEN DES SERVERS ÜBEREINANDER und baut die Regel
 * nicht nach: „wen trifft es" heisst „er ist in MINDESTENS EINER der
 * gewählten Gruppen", und die Vereinigung der Gruppenlisten ist genau
 * dieses ODER. Was WIRKLICH gilt, rechnet der Server (`betroffen`).
 */
function FaktorPflicht({ stand, gruppen, unbekannt, neuLaden }: any) {
  const jetzt: string[] = Array.isArray(stand) ? stand : [];
  const zeilen: any[] = Array.isArray(gruppen) ? gruppen : [];
  const [gewaehlt, setGewaehlt] = useState<string[]>(jetzt);
  const [klar, setKlar] = useState(false);
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<Fehlerlage | null>(null);
  const [fertig, setFertig] = useState<string | null>(null);
  // Der Stand kommt als Feld herein und ist bei jedem Zeichnen ein NEUES
  // Objekt. Verglichen wird deshalb sein INHALT - eine Abhängigkeit auf das
  // Feld selbst setzte die Auswahl bei jedem Zeichnen zurück (dieselbe Falle
  // wie in `useEntwurf`, 31.08.2026).
  const standText = jetzt.join(',');
  useEffect(() => { setGewaehlt(jetzt); setKlar(false); },
            // eslint-disable-next-line react-hooks/exhaustive-deps
            [standText]);

  const an = (s: string) => gewaehlt.includes(s);
  const umschalten = (s: string) => {
    setKlar(false);
    setGewaehlt(an(s) ? gewaehlt.filter((x) => x !== s) : [...gewaehlt, s]);
  };

  const gleich = [...gewaehlt].sort().join(',') === [...jetzt].sort().join(',');

  // NEU BETROFFEN IST, WEN DIE ÄNDERUNG TRIFFT UND DIE HEUTIGE EINSTELLUNG
  // NICHT. Wer schon heute einen bräuchte, ist beim nächsten Anmelden
  // ohnehin dran; ihn hier noch einmal aufzuzählen liesse jede Änderung
  // gefährlicher aussehen, als sie ist.
  const namenAus = (welche: string[]) => {
    const s = new Set<string>();
    for (const z of zeilen) {
      if (welche.includes(z.schluessel)) for (const n of z.ohneApp ?? []) s.add(n);
    }
    return s;
  };
  const alt = namenAus(jetzt);
  const wenn = [...namenAus(gewaehlt)].filter((n) => !alt.has(n));
  const trifft = gleich ? 0 : wenn.length;

  async function umlegen() {
    setLaeuft(true); setFehler(null); setFertig(null);
    try {
      // Leer heisst aus, wie überall in diesem Haus - und `konfigAendern`
      // macht aus dem leeren Text NULL.
      await api.konfigAendern('sicherheit.zweiter_faktor_pflicht',
                              gewaehlt.join(','), klar);
      setKlar(false);
      setFertig(gewaehlt.length
        ? `Der zweite Faktor ist jetzt Pflicht für: ${gewaehlt.join(', ')}.`
        : 'Der zweite Faktor ist für niemanden mehr Pflicht.');
      neuLaden?.();
    } catch (f) { setFehler(ausFehler(f)); }
    finally { setLaeuft(false); }
  }

  return (
    <Karte titel="2FA-Pflicht">
      <Fehlerkasten lage={fehler} />
      {fertig && <div className="hinweis">{fertig}</div>}
      <p className="klein leise" style={{ marginTop: 0 }}>
        Zurzeit Pflicht für: <b>{jetzt.length ? jetzt.join(', ') : 'niemanden'}</b>.
        Mehrere Gruppen sind erlaubt; wer in einer davon ist, braucht einen.
        Wer eine Gruppe dazunimmt, schickt jeden darin ohne App beim nächsten
        Anmelden in den Einrichtungsschritt - unter Umständen auf der
        Baustelle, ohne zweites Gerät.
      </p>
      {unbekannt?.length > 0 && (
        <div className="hinweis warnung">
          In der Einstellung steht {unbekannt.length === 1 ? 'ein Name' : 'Namen'},
          {' '}zu {unbekannt.length === 1 ? 'dem' : 'denen'} es keine Gruppe
          {' '}gibt: {unbekannt.join(', ')}. {unbekannt.length === 1 ? 'Er trifft' : 'Sie treffen'}
          {' '}niemanden.
        </div>
      )}
      <table className="liste">
        <thead>
          <tr><th style={{ width: 40 }} />
              <th style={{ width: 190 }}>Gruppe</th>
              <th className="zahl" style={{ width: 110 }}>Zugänge</th>
              <th className="zahl" style={{ width: 110 }}>ohne App</th>
              <th>wen es trifft</th></tr>
        </thead>
        <tbody>
          {zeilen.map((z) => (
            <tr key={z.schluessel} style={{ cursor: 'pointer' }}
                onClick={() => umschalten(z.schluessel)}>
              <td>
                <input type="checkbox" checked={an(z.schluessel)}
                       onChange={() => umschalten(z.schluessel)} />
              </td>
              <td>
                <b>{z.name}</b>
                <div className="klein leise"><code>{z.schluessel}</code></div>
                {jetzt.includes(z.schluessel) && (
                  <div className="klein leise">gilt zurzeit</div>
                )}
              </td>
              <td className="zahl">{z.zugaenge}</td>
              <td className="zahl">
                {/* Wer schon eine App hat, merkt vom Umlegen nichts. Gezählt
                    werden nur die, die dann eine bräuchten und keine haben. */}
                {z.ohneApp?.length > 0
                  ? <b className="rot">{z.ohneApp.length}</b>
                  : <span className="leise">0</span>}
              </td>
              <td className="klein leise">
                {z.ohneApp?.length
                  ? z.ohneApp.slice(0, 6).join(', ')
                    + (z.ohneApp.length > 6
                        ? ` und ${z.ohneApp.length - 6} weitere` : '')
                  : 'niemanden - alle in dieser Gruppe haben schon eine App.'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {!gleich && (
        <div className={'hinweis ' + (trifft > 0 ? 'fehler' : '')}
             style={{ marginTop: 8 }}>
          {trifft > 0
            ? `${trifft} ${trifft === 1 ? 'Person hat' : 'Personen haben'} `
              + 'noch keine App eingerichtet und '
              + `${trifft === 1 ? 'wird' : 'werden'} beim nächsten Anmelden `
              + 'dazu aufgefordert: '
              + wenn.slice(0, 12).join(', ')
              + (wenn.length > 12 ? ` und ${wenn.length - 12} weitere` : '')
              + '.'
            : 'Davon ist niemand neu betroffen: alle, die dann einen '
              + 'bräuchten, haben schon eine App eingerichtet.'}
          <label style={{ display: 'flex', gap: 8, marginTop: 6,
                          alignItems: 'center', cursor: 'pointer' }}>
            <input type="checkbox" checked={klar}
                   onChange={(e) => setKlar(e.target.checked)} />
            Mir ist klar, was das bedeutet.
          </label>
          <div className="reihe" style={{ marginTop: 8 }}>
            <button className="knopf klein haupt" disabled={laeuft || !klar}
                    onClick={umlegen}>
              {gewaehlt.length
                ? `Pflicht für ${gewaehlt.length} `
                  + (gewaehlt.length === 1 ? 'Gruppe' : 'Gruppen') + ' setzen'
                : 'Pflicht abschalten'}
            </button>
            <button className="knopf klein" disabled={laeuft}
                    onClick={() => { setGewaehlt(jetzt); setKlar(false); }}>
              zurück
            </button>
          </div>
        </div>
      )}
    </Karte>
  );
}


function FaktorUebersicht() {
  const [a, setA] = useState<any | null>(null);
  /* ZURÜCKSETZEN (.275). Marvin, 01.10.2026: „Ein Partner hat
     Zugriffsprobleme und ich weiß nicht warum." */
  const [hinweis, setHinweis] = useState<string | null>(null);
  const [laeuftZ, setLaeuftZ] = useState(false);
  async function zuruecksetzen(x: any) {
    if (!await fragen(`Den zweiten Faktor von ${x.name} zurücksetzen? Er richtet ihn bei der nächsten Anmeldung neu ein.`)) return;
    setLaeuftZ(true);
    try { const r = await api.faktorZuruecksetzen(x.benutzer_id); setHinweis(r.klartext); await laden(); }
    catch (f: any) { setHinweis(f?.message ?? String(f)); }
    finally { setLaeuftZ(false); }
  }
  const laden = () => api.faktorUebersicht().then(setA).catch(() => setA(null));
  useEffect(() => { laden(); }, []);
  useFrisch(laden);
  const l: any[] = a?.benutzer ?? [];
  if (!l.length) return null;
  const ohne = l.filter((x) => x.faktoren === 0).length;
  return (
    <>
    <FaktorPflicht stand={a.stand} gruppen={a.gruppen} unbekannt={a.unbekannt}
                   neuLaden={laden} />
    <Karte titel={`Wer ist wie abgesichert (${l.length})`}>
      {hinweis && <div className="hinweis">{hinweis}</div>}
      <p className="klein leise" style={{ marginTop: 0 }}>
        Die Frage, die vor dem Scharfschalten der Pflicht zu stellen ist: wen
        sperren wir damit aus. {ohne > 0
          ? `Zurzeit ${ohne} Benutzer ohne zweiten Faktor.`
          : 'Alle haben einen zweiten Faktor.'}
      </p>
      <table className="liste">
        <thead>
          <tr><th style={{ width: 200 }}>Name</th>
          <th style={{ width: 130 }}>Passwort</th>
          <th style={{ width: 150 }}>zweiter Faktor</th>
          <th style={{ width: 150 }}>zuletzt benutzt</th>
          <th className="zahl" style={{ width: 110 }}>Notfallcodes</th>
          <th className="zahl" style={{ width: 90 }}>Geräte</th>
          <th>letzter Fehlversuch</th>
          <th style={{ width: 120 }}></th></tr>
        </thead>
        <tbody>
          {l.map((x) => (
            <tr key={x.benutzer_id} style={{ cursor: 'default' }}>
              <td><b>{x.name}</b>
                {x.ist_sub && <div className="klein leise">Partnerfirma</div>}</td>
              <td className="klein">
                {x.hat_passwort
                  ? 'gesetzt'
                  : <span className="rot">kein Passwort</span>}
              </td>
              <td>
                <span className={'marke-zustand z-'
                  + (x.faktoren > 0 ? 'erledigt' : 'ueberfaellig')}>
                  {x.faktoren > 0 ? `${x.faktoren} eingerichtet` : 'keiner'}
                </span>
              </td>
              <td className="klein leise">{zeitpunkt(x.faktor_zuletzt)}</td>
              <td className="zahl">{x.codes_offen}</td>
              <td className="zahl">{x.geraete}</td>
              <td className="klein">
                {x.fehl_am
                  ? <><span className="rot">{x.fehl_grund ?? 'Code falsch'}</span>
                      <div className="leise">{zeitpunkt(x.fehl_am)}</div></>
                  : <span className="leise">-</span>}
              </td>
              <td>
                {x.faktoren > 0 && (
                  <button className="knopf klein" disabled={laeuftZ} onClick={() => zuruecksetzen(x)}>
                    zurücksetzen
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </Karte>
    </>
  );
}


/**
 * Die Konfiguration.
 *
 * Marvin, 23.08.2026: „eintragungen in der konfiguration anzupassen. also im
 * moment ist es alles nur lesend. das ist für den livebetrieb nicht richtig."
 *
 * Jeder Schluessel ist hier aenderbar. Einige sind es nicht im Vorbeigehen:
 * Testmodus, Behoerdenversand, Telegram-Testmodus, die 2FA-Pflicht und das
 * Token des Mailzustellers. Wer einen davon umlegt, bestaetigt das
 * ausdruecklich, und die Bestaetigung steht danach im Protokoll. Am anderen
 * Ende sitzen Behoerden und Kunden.
 *
 * WELCHE DAS SIND, STEHT NICHT HIER. Diese Datei fuehrte bis zum 26.08.2026
 * eine eigene Abschrift der Liste aus `api/src/regeln/konfiguration.ts`. Als
 * dort zwei Schluessel dazukamen, verlangte der Server fuer sie eine
 * Bestaetigung, die diese Maske gar nicht anbot: der Schalter liess sich
 * nicht mehr umlegen, und die Fehlermeldung sagte nur, dass eine Bestaetigung
 * fehlt. Jetzt kommen `gefahr_harmlos` und `gefahr_was` mit der Antwort mit.
 */
/**
 * WAS HIER NICHT STEHT, UND WO ES STEHT.
 *
 * ORIGINALTEXT MARVIN (02.09.2026)
 *   „hast du auch die alten „falschen" orte überall rausgefiltert? was mich
 *    zu der frage bringt, wo trage ich dann in der konfiguration die
 *    ortsnetze ein, die er rausfiltern soll?"
 *
 * ER HAT AN DER RICHTIGEN STELLE GESUCHT UND DAS FALSCHE ERWARTET. Die Orte,
 * in denen wir nicht arbeiten, sind keine EINSTELLUNG, sondern eine LISTE mit
 * einer Zeile je Ort - sie stehen in den Stammdaten. Ein zweiter Weg hierher
 * wäre kein doppelter Komfort, sondern eine halbe Auskunft: wer den einen
 * kennt, erfährt vom anderen nie (siehe die Vordrucke, 27.08.2026).
 *
 * WER NICHTS FINDET, SOLL WENIGSTENS ERFAHREN, WO ER SUCHEN MUSS. Diese
 * Liste erscheint nur beim SUCHEN und nur, wenn das gesuchte Wort auf einen
 * dieser Fälle passt. Ein Kasten, der immer dasteht, wird nach zwei Wochen
 * nicht mehr gelesen.
 */
const WOANDERS: { passt: RegExp; was: string; wo: string }[] = [
  /* DIE ORTE STANDEN HIER ALS „ist keine Einstellung und steht woanders" -
     und zwar viermal vergeblich (02.09., 03.09., 07.09., 08.09.2026). Seit
     dem 08.09.2026 stehen sie WIRKLICH hier: Reiter „Betrieb", Karte „Orte,
     in denen wir nicht arbeiten". Ein Wegweiser, der auf denselben Bildschirm
     zeigt, auf dem er steht, ist einer zu viel - deshalb ist die Zeile weg
     und nicht umgeschrieben.

     WAS BLEIBT, IST DIE GEMEINDEZUORDNUNG: das ist eine andere Frage (welche
     Behörde bekommt die Post) und steht weiter in den Stammdaten. Sie hat
     ihren eigenen Eintrag weiter unten. */
  { passt: /onkz|ortsnetz|gebiet|kapazit/i,
    was: 'Die Ortsnetzkennzahlen und ihre Gebiete',
    wo: 'Kapazität → Gebiete.' },
  { passt: /vorlage|vordruck|formular|regelplan/i,
    was: 'Die Vordrucke der Behörden und die Regelpläne',
    wo: 'Behörden → Vorlagen.' },
  { passt: /position|leistung|katalog|\boz\b|preis/i,
    was: 'Der Leistungskatalog',
    wo: 'Stammdaten → Leistungspositionen.' },
  { passt: /platzhalter|marke|feld/i,
    was: 'Der Platzhalterkatalog der Behördenpapiere',
    wo: 'Stammdaten → Platzhalter.' },
  { passt: /gemeinde|behörde|behoerde/i,
    was: 'Die Gemeinden mit ihren Mailadressen und Vorlaufzeiten',
    wo: 'Stammdaten → Gemeinden.' },
];

export function Konfiguration({ ich }: { ich?: Anmelder }) {
  /* HIER GAB ES ZWEI REITER, und der zweite war das Handbuch.
     Marvin, 27.08.2026: „das handbuch kannst du raus als richtigen menüpunkt
     machen." Er hat recht, und es war schlimmer als unauffindbar: wer kein
     Recht auf die Konfiguration hat, kam GAR NICHT an die Anleitung - also
     kein Monteur und keine Partnerbauleitung. Seit Migration 052 ist es ein
     eigener Menüpunkt mit eigenem Leserecht (`seiten/Handbuch.tsx`). */
  const [k, setK] = useState<any[]>([]);
  const [suche, setSuche] = useState('');
  const [tgProbe, setTgProbe] = useState(false);
  const [logo, setLogo] = useState(false);
  const [fehler, setFehler] = useState<Fehlerlage | null>(null);
  const [meldung, setMeldung] = useState<string | null>(null);
  const [probe, setProbe] = useState(false);
  const [reiter, setReiter] = useState('Betrieb');

  const pflegen = !ich || darf(ich, 'konfiguration.pflegen');
  const laden = () => api.konfiguration().then(setK)
    .catch((f) => setFehler(ausFehler(f)));
  useEffect(() => { laden(); }, []);
  useFrisch(laden);

  /* SECHS REITER STATT NEUNZIG ZEILEN.
   *
   * ORIGINALTEXT MARVIN (27.08.2026)
   *   „brauchen wir unter „konfiguration" die ganzen schlüssel noch? das ganze
   *    ist für mich so unübersichtlich. bringe es mit in mein aktuelles
   *    design. oben brauchen wir die reiter."
   *
   * DIE REITERLEISTE IST DIESELBE WIE ÜBERALL (`.reiter`, siehe stil.css). Das
   * ist der Teil „mein aktuelles Design": die Akte, das Aufmaß und die
   * Stammdaten haben sie seit jeher, nur dieser Bildschirm nicht - er warf
   * sechzehn Karten untereinander auf die Seite.
   *
   * DIE REIHENFOLGE KOMMT AUS DER MIGRATION UND NICHT AUS DEM ALPHABET.
   * „Betrieb" zuerst, „Schnittstellen" zuletzt: das ist die Reihenfolge, in
   * der man einen Betrieb einrichtet. Ein Reiter, den die Migration nicht
   * kennt (etwa „Sonstiges"), rutscht hinten an - und dass er dasteht, ist
   * die Aufforderung, ihn einzuordnen. */
  const REITERFOLGE = ['Betrieb', 'Sicherheit', 'Nachrichten',
                       'Termine und Fristen', 'Bau und Abrechnung',
                       'Schnittstellen'];
  const reiterliste = [...new Set(k.map((x) => x.reiter ?? 'Sonstiges'))]
    .sort((a, b) => {
      const ia = REITERFOLGE.indexOf(a), ib = REITERFOLGE.indexOf(b);
      return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib) || a.localeCompare(b, 'de');
    });

  /* EINE SUCHE SUCHT ÜBER ALLES, auch über die anderen Reiter. Sonst ist der
     Reiter eine Falle: man tippt einen Schlüssel ein, sieht nichts und
     schliesst daraus, dass es ihn nicht gibt - während er einen Reiter weiter
     steht. Wird gesucht, treten die Reiter deshalb zurück. */
  const passt = (x: any) =>
    x.schluessel.toLowerCase().includes(suche.toLowerCase()) ||
    (x.titel ?? '').toLowerCase().includes(suche.toLowerCase()) ||
    (x.zweck ?? '').toLowerCase().includes(suche.toLowerCase());
  const gefiltert = suche
    ? k.filter(passt)
    : k.filter((x) => (x.reiter ?? 'Sonstiges') === reiter);
  const bereiche = [...new Set(gefiltert.map((x) => x.bereich))];
  const leer = k.filter((x) => !x.gesetzt && x.pflicht).length;
  const jeReiter = (r: string) =>
    k.filter((x) => (x.reiter ?? 'Sonstiges') === r).length;
  const fehltJeReiter = (r: string) =>
    k.filter((x) => (x.reiter ?? 'Sonstiges') === r && x.pflicht && !x.gesetzt)
     .length;

  const stand = (s: string) => k.find((x) => x.schluessel === s)?.wert;
  const testmodus = stand('betrieb.testmodus') !== 'nein';

  return (
    <>
      <Kopf
        titel="Konfiguration"
        weg={`${k.length} Schlüssel${leer ? `, ${leer} Pflichtfelder ohne Wert` : ''}`}
        rechts={(
          <div className="reihe">
            <input type="search" placeholder="Schlüssel oder Zweck" value={suche}
                   onChange={(e) => setSuche(e.target.value)} style={{ width: 240 }} />
            {pflegen && (
              <>
                <button className="knopf" onClick={() => setProbe(true)}>
                  Probemail
                </button>
                {/* UND EINE FÜR TELEGRAM.
                    Marvin, 27.08.2026: „Ist die Anbindung zu telegram
                    vorbereitet?" Sie war es - und der Bot-Token wurde aus
                    einem Schlüssel gelesen, den keine Migration anlegte und
                    den diese Maske wegen ihres Namensmusters auch nicht
                    anlegen konnte. Migration 049 legt ihn an, dieser Knopf
                    sagt, ob er stimmt. */}
                <button className="knopf" onClick={() => setTgProbe(true)}>
                  Telegram prüfen
                </button>
                {/* Marvin, 27.08.2026: „wo lade ich mein logo hoch". Bis
                    heute: gar nirgends - das Zeichen lag im Bündel und ein
                    neues brauchte eine Lieferung. */}
                <button className="knopf" onClick={() => setLogo(true)}>
                  Logo
                </button>
              </>
            )}
          </div>
        )}
      />

      {/* OBEN DIE REITER, wie auf jedem anderen Bildschirm dieses Systems.
          Beim Suchen treten sie zurück: eine Suche, die nur den aktuellen
          Reiter durchsieht, behauptet, es gäbe einen Schlüssel nicht, der
          einen Reiter weiter steht. */}
      {!suche && (
        <div className="reiter">
          {reiterliste.map((r) => (
            <button key={r} onClick={() => setReiter(r)}
                    aria-current={reiter === r}>
              {r}
              <span className="reiterzahl">{jeReiter(r)}</span>
              {fehltJeReiter(r) > 0 && (
                <span className="merker fehler">{fehltJeReiter(r)}</span>
              )}
            </button>
          ))}
        </div>
      )}

      {/* DAS BAND DER BEREICHE, und der Chip sagt, wo man gerade steht.
          Unter einem Reiter liegen bis zu sechs Karten; wer die dritte
          sucht, rollt und liest Überschriften. Beim Suchen steht es nicht
          da: dann sind die Karten das Ergebnis einer Suche und keine
          Gliederung. */}
      {!suche && (
        <Abschnittsband abschnitte={bereiche.map((bz) => ({
          id: 'bereich-' + kennung(bz), titel: bz,
        }))} />
      )}

      <div className="inhalt">
        <Fehlerkasten lage={fehler} />
        {/* DIE SICHERUNG (.332) steht oben im Reiter Betrieb, über den Karten. */}
        {!suche && reiter === 'Betrieb' && pflegen && <Sicherung />}
        {!suche && reiter === 'Betrieb' && pflegen && <Update />}
        {meldung && <div className="hinweis">{meldung}</div>}

        {suche && (
          <div className="hinweis">
            Gesucht wird über ALLE Reiter: {gefiltert.length} von {k.length}
            {' '}Schlüsseln passen auf „{suche}".
          </div>
        )}

        {suche && WOANDERS.filter((w) => w.passt.test(suche)).map((w) => (
          <div className="hinweis" key={w.was}>
            <b>{w.was}</b> ist keine Einstellung und steht nicht hier: {w.wo}
          </div>
        ))}

        <div className={'hinweis ' + (testmodus ? '' : 'fehler')}>
          {testmodus
            ? 'Der Testmodus ist an: Mails gehen an die Testadresse, nicht an '
              + 'Behörden oder Kunden. So bleibt es, bis betrieb.testmodus '
              + 'ausdrücklich auf "nein" gesetzt wird.'
            : 'ACHTUNG: der Testmodus ist AUS. Mails gehen an die echten '
              + 'Empfänger.'}
        </div>

        {bereiche.map((bz) => (
          <Karte key={bz} titel={bz} id={'bereich-' + kennung(bz)}>
            {/* KEINE TABELLE MEHR. Eine Einstellung ist ein FORMULARFELD und
                keine Datenzeile: sie hat einen Namen, eine Erklärung und eine
                Bedienung, und die drei stehen nicht in Spalten, sondern
                nebeneinander. Auf dem Telefon rutscht das Feld unter den
                Namen (`.einstellung` in stil.css) - vier Tabellenspalten auf
                375 Pixeln haben den Wert buchstabenweise umbrochen und den
                Knopf aus der Karte geschoben. */}
            <div className="einstellungen">
              {gefiltert.filter((x) => x.bereich === bz).map((x) => (
                <Einstellung
                  key={x.schluessel} x={x} pflegen={pflegen}
                  gespeichert={(text: string) => { setMeldung(text); laden(); }}
                />
              ))}
            </div>
            {/* DER KNOPF, DEN DER ZWECKTEXT SEIT MIGRATION 020 VERSPRICHT.
                „Ob ein Eintrag trägt, sagt „Dienste prüfen" mit einem ECHTEN
                Testbild" - der Weg stand auf dem Server, und keine Zeile der
                Oberfläche hat ihn gerufen. Ein Hinweis, der einen Knopf
                nennt, den es nicht gibt, ist schlimmer als kein Hinweis. */}
            {bz === 'Karte' && pflegen && <Kartendienste />}
            {/* .569: Marvin, 01.11.2026: „stimmen die Zugangsdaten?" Anmelden, nachsehen, abmelden. */}
            {bz === 'E-Vergabe' && pflegen && <EvergabePruefen />}
            {/* DIE ORTSSPERRE, IN DEN EINSTELLUNGEN.
                ORIGINALTEXT MARVIN (08.09.2026): „derpunkt soll in die
                einstellungen, und das soll imer und überall immer abgeglichen
                werden". Sie ist kein Konfigurationsschlüssel - eine Liste mit
                einer Zeile je Ort in einen Wert zu schreiben wäre eine ZWEITE
                Wahrheit neben `ort.ignoriert`, an dem der Importfilter, die
                Archivierung und die Auswertung hängen. Sie ist ein WERKZEUG in
                dieser Karte, dieselbe Bauart wie „Dienste prüfen". */}
            {bz === 'Orte, in denen wir nicht arbeiten' && ich
              && <Ortssperre ich={ich} />}
          </Karte>
        ))}
        {gefiltert.length === 0 && (
          <Leer text={suche
            ? `Kein Schlüssel passt auf „${suche}".`
            : `Unter „${reiter}" steht kein Schlüssel.`} />
        )}

        {probe && (
          <Probemail zu={() => setProbe(false)}
                     testadresse={stand('betrieb.mail_testadresse') ?? ''} />
        )}
        {tgProbe && <Telegramprobe zu={() => setTgProbe(false)} />}
        {logo && <Firmenzeichen zu={() => setLogo(false)} />}
      </div>
    </>
  );
}


/**
 * Eine Einstellung.
 *
 * ORIGINALTEXT MARVIN (31.08.2026)
 *   „bei der konfiguration weiß ich nicht ob wie sie in dieser form brauchen,
 *    aber umbauen auf das plattform design mit der technik der
 *    programmiersprachen."
 *
 * DIE BEDIENUNG FOLGT DER ART UND WIRD NICHT MEHR GERATEN.
 *
 * Bis Migration 070 hing alles am WERT: ein Schlüssel war ein Schalter, wenn
 * zufällig eine `auswahl` mit „ja" und „nein" dabeistand - bei vier
 * Schlüsseln stand sie nicht dabei, und der Hauptschalter der Automatik war
 * ein Textfeld. Ein Brieftext von einer halben Seite wurde in einer
 * EINZEILIGEN Eingabe bearbeitet. Eine Zahl hatte keine Einheit und keine
 * Grenzen.
 *
 * Jetzt steht `art` in der Datenbank, der CHECK dort weist ab, was nicht
 * passt, und hier wird gezeichnet, was dazugehört.
 *
 * UND ES GIBT KEIN „ÄNDERN" MEHR. Der Weg war: Knopf drücken, Zeile klappt
 * auf, Wert eintippen, „ok" drücken - vier Schritte für eine Zahl. Das Feld
 * steht jetzt einfach da; die zwei Knöpfe erscheinen erst, wenn wirklich
 * etwas anders ist. Ein Schalter und eine Auswahl speichern sofort: dort gibt
 * es nichts zu tippen, was man noch verwerfen wollte.
 */
function Einstellung({ x, pflegen, gespeichert }: any) {
  const original = x.wert ?? '';
  const [wert, setWert] = useState(original);
  const [klar, setKlar] = useState(false);
  /* OB DAS GEHEIMNIS OFFEN DASTEHT. Wer sich einen Wert gerade ERZEUGT hat,
     muss ihn abschreiben können - er steht nirgends sonst. Ein Geheimnis
     hinter Punkten, das man selbst gerade gewürfelt hat, ist unbrauchbar. */
  const [klarSehen, setKlarSehen] = useState(false);
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<Fehlerlage | null>(null);

  // Kommt ein neuer Stand vom Server, folgt das Feld - aber nur, wenn gerade
  // niemand darin tippt. Sonst verschwindet die Eingabe unter der Hand.
  const [zuletzt, setZuletzt] = useState(original);
  if (zuletzt !== original && wert === zuletzt) {
    setZuletzt(original); setWert(original);
  }

  // Beides kommt vom Server (siehe Kopf dieser Datei). `gefahr_harmlos` darf
  // der leere Text sein - `eingang.mail_token` ist genau dann harmlos, wenn
  // es leer ist -, deshalb wird auf null geprüft und nicht auf Wahrheit.
  const gefahr: { harmlos: string; was: string; wachsend: boolean } | null =
    x.gefahr_was ? { harmlos: x.gefahr_harmlos ?? '', was: x.gefahr_was,
                     wachsend: !!x.gefahr_wachsend } : null;
  /* WELCHER SCHRITT DER GEFÄHRLICHE IST, SAGT AUCH DER SERVER. Bei einer
     LISTE ist es jede Erweiterung und nicht nur der erste Eintrag: wer zu
     `admin` die `sub_monteur` dazunimmt, sperrt in dem Moment die Monteure
     auf der Baustelle aus. DER RIEGEL BLEIBT IM SERVER (`braucht`), das hier
     ist die Höflichkeit - er weist ohne Bestätigung ohnehin mit 409 ab. */
  const teileVon = (s: string) =>
    s.split(',').map((t) => t.trim()).filter(Boolean);
  const gefaehrlichJetzt = !gefahr ? false
    : gefahr.wachsend
      ? teileVon(wert).some((t) => !teileVon(original).includes(t))
      : wert !== gefahr.harmlos && original === gefahr.harmlos;
  const auswahl: string[] = Array.isArray(x.auswahl) ? x.auswahl : [];
  const art: string = x.art ?? 'text';
  const schmutzig = wert !== original;

  async function schreibe(neuerWert: string, mitKlar = false) {
    setLaeuft(true); setFehler(null);
    try {
      await api.konfigAendern(x.schluessel,
                              neuerWert === '' ? null : neuerWert, mitKlar);
      setKlar(false);
      gespeichert(`${x.titel ?? x.schluessel} gespeichert.`);
    } catch (f) { setFehler(ausFehler(f)); }
    finally { setLaeuft(false); }
  }

  /* EIN SCHALTER, AN DEM ETWAS HÄNGT, IST KEIN KLICK.
     `betrieb.testmodus` auf „nein" schaltet Behördenpost scharf. Trägt der
     Schlüssel eine Gefahr, wird der Kasten aufgeklappt und ausdrücklich
     bestätigt - so etwas darf nicht auch danebengehen können. */
  const sofort = (art === 'schalter' || art === 'auswahl') && !gefahr
                 && !x.geheim && pflegen;

  const falsch = fehler?.feld === 'wert' ? 'falsch' : undefined;

  /* DAS FREIE FELD STEHT EINMAL DA. Es ist der letzte Zweig des `switch`
     unten UND der Rückfall der Liste ohne Auswahl; zwei Abschriften liefen
     beim ersten Nachtrag auseinander, und dann sähe dieselbe Einstellung je
     nach Weg anders aus. */
  const freiesFeld = () => (
    <input value={wert} disabled={!pflegen || laeuft} className={falsch}
           style={{ width: '100%' }}
           inputMode={art === 'mail' || art === 'mailliste'
                       ? 'email' : undefined}
           placeholder={art === 'adresse' ? 'https://…'
                      : art === 'mailliste' ? 'name@firma.de, zweite@firma.de'
                      : art === 'liste' ? 'Wert, Wert, Wert'
                      : ''}
           onChange={(e) => setWert(e.target.value)} />
  );

  const feld = () => {
    if (x.geheim) {
      return (
        <div className="reihe" style={{ gap: 8 }}>
          <input type={klarSehen ? 'text' : 'password'} value={wert}
                 disabled={!pflegen || laeuft}
                 className={falsch} style={{ flex: 1, minWidth: 0 }}
                 placeholder={x.gesetzt ? 'gesetzt - neuer Wert überschreibt'
                                        : 'leer'}
                 onChange={(e) => setWert(e.target.value)} />
          {/* EIN WERT, DEN MAN SICH AUSDENKEN MUSS, BEKOMMT EINEN KNOPF.
              Marvin, 06.09.2026: „wo bekomme ich den n8n token her?" - für
              die Hälfte dieser Schlüssel lautet die Antwort „den denkt man
              sich aus", und bis heute stand das als Satz in der
              Anbindungsliste und nirgends als Handgriff. **Ein ausgedachtes
              Token ist so gut wie das, was demjenigen gerade einfiel.**
              Erzeugt wird er IM BROWSER (`teile/zufallswert.ts`): ein Weg,
              der ein Geheimnis ausliefert, stünde in jedem Protokoll
              dazwischen. */}
          {pflegen && (
            <button type="button" className="knopf" disabled={laeuft}
                    title="Einen langen Zufallswert erzeugen"
                    onClick={() => { setWert(zufallswert()); setKlarSehen(true); }}>
              Wert erzeugen
            </button>
          )}
          {/* UND WER IHN ERZEUGT HAT, MUSS IHN ABSCHREIBEN KÖNNEN. Ein
              Geheimnis hinter Punkten, das man gerade selbst erzeugt hat,
              ist unbrauchbar - es steht nirgends sonst. */}
          {pflegen && wert !== '' && (
            <button type="button" className="knopf"
                    onClick={() => setKlarSehen(!klarSehen)}>
              {klarSehen ? 'verbergen' : 'zeigen'}
            </button>
          )}
        </div>
      );
    }
    switch (art) {
      case 'schalter':
        return (
          <label className="wahlzeile" style={{ cursor: pflegen ? 'pointer' : 'default' }}>
            <input type="checkbox" checked={wert === 'ja'}
                   disabled={!pflegen || laeuft}
                   onChange={(e) => {
                     const w = e.target.checked ? 'ja' : 'nein';
                     setWert(w);
                     if (sofort) schreibe(w);
                   }} />
            <span><b>{wert === 'ja' ? 'ja' : 'nein'}</b></span>
          </label>
        );

      case 'auswahl':
        return (
          <select value={wert} disabled={!pflegen || laeuft} className={falsch}
                  style={{ width: '100%', maxWidth: 260 }}
                  onChange={(e) => {
                    setWert(e.target.value);
                    if (sofort) schreibe(e.target.value);
                  }}>
            {!x.pflicht && <option value="">leer</option>}
            {auswahl.map((w) => <option key={w} value={w}>{w}</option>)}
            {/* Ein Wert, der von früher dasteht und nicht mehr in der Liste
                ist, wird angezeigt und nicht stillschweigend verschluckt. */}
            {wert !== '' && !auswahl.includes(wert) && (
              <option value={wert}>{wert} (nicht mehr vorgesehen)</option>
            )}
          </select>
        );

      case 'liste':
        /* EINE LISTE MIT EINER AUSWAHL IST EINE MEHRFACHAUSWAHL.
         *
         * ORIGINALTEXT MARVIN (03.09.2026)
         *   „2FA auch einzeln für die gruppen aktivierbar, merhfachauswahl
         *    zulässig"
         *
         * Hier stand ein Textfeld mit „Wert, Wert, Wert" darin - also die
         * Aufforderung, sechs Gruppenschlüssel von Hand abzuschreiben, jeden
         * mit der Gelegenheit zu einem Tippfehler. Der Riegel dahinter
         * greift zwar (`pruefeArt` prüft JEDEN Eintrag gegen die Auswahl),
         * aber ein Riegel ist keine Bedienung.
         *
         * OHNE `auswahl` BLEIBT ES EIN TEXTFELD. `postfach.eigene_domains`
         * und `EINBLASSTOP_SEITEN` sind freie Listen - dort gibt es nichts
         * anzukreuzen, und eine Auswahl mit null Einträgen wäre ein leerer
         * Kasten.
         */
        if (!auswahl.length) return freiesFeld();
        return (
          <div className="hakenraster">
            {auswahl.map((w) => {
              const teile = wert.split(',').map((s) => s.trim()).filter(Boolean);
              const an = teile.includes(w);
              return (
                <label key={w} className="wahlzeile"
                       style={{ cursor: pflegen ? 'pointer' : 'default' }}>
                  <input type="checkbox" checked={an}
                         disabled={!pflegen || laeuft}
                         onChange={() => {
                           /* Die Reihenfolge der AUSWAHL, nicht die des
                              Anklickens: sonst steht derselbe Satz Gruppen je
                              nach Klickfolge anders da, und zwei gleiche
                              Einstellungen sehen verschieden aus. */
                           const neu = auswahl.filter(
                             (k) => (k === w ? !an : teile.includes(k)));
                           setWert(neu.join(','));
                         }} />
                  <span>{w}</span>
                </label>
              );
            })}
            {/* Was von früher dasteht und nicht mehr in der Auswahl ist, wird
                angezeigt und nicht stillschweigend verschluckt - dieselbe
                Regel wie bei der einfachen Auswahl. */}
            {wert.split(',').map((s) => s.trim()).filter(Boolean)
                 .filter((s) => !auswahl.includes(s))
                 .map((s) => (
              <span key={s} className="klein leise">
                {s} (nicht mehr vorgesehen)
              </span>
            ))}
          </div>
        );

      case 'zahl':
      case 'kommazahl':
        /* DIE EINHEIT STEHT AM FELD UND NICHT IM FLIESSTEXT DANEBEN. „14"
           allein ist keine Auskunft; „14 Tage" ist eine. Und die Grenzen
           stehen darunter, damit man sie kennt, BEVOR der Server abweist. */
        return (
          <div>
            <div className="reihe" style={{ gap: 6, alignItems: 'center' }}>
              <input type="number" value={wert} disabled={!pflegen || laeuft}
                     className={falsch} style={{ width: 120 }}
                     step={art === 'kommazahl' ? 'any' : 1}
                     min={x.min_wert ?? undefined} max={x.max_wert ?? undefined}
                     onChange={(e) => setWert(e.target.value)} />
              {x.einheit && <span className="klein leise">{x.einheit}</span>}
            </div>
            {(x.min_wert !== null || x.max_wert !== null) && (
              <div className="klein leise" style={{ marginTop: 2 }}>
                {x.min_wert ?? '?'} bis {x.max_wert ?? '?'}
              </div>
            )}
          </div>
        );

      case 'datum':
        /* EIN DATUM IST EIN DATUMSFELD, KEIN TEXTFELD.
           Marvin, 01.09.2026: „datum im falschen format." Die Art `datum`
           gibt es seit Migration 070; dieser `switch` kannte sie nicht, und
           sie fiel bis heute in den letzten Zweig - ein gewöhnliches
           Textfeld, in dem der Rohwert steht. Was der Browser darin anzeigt,
           richtet sich nach SEINER Sprache; der Wert dahinter bleibt ISO,
           und genau das erwartet die Datenbank (`konfiguration_wert_passt`). */
        return (
          <input type="date" value={wert} disabled={!pflegen || laeuft}
                 className={falsch} style={{ width: 200 }}
                 onChange={(e) => setWert(e.target.value)} />
        );

      case 'langtext':
        /* EIN BRIEF GEHÖRT NICHT IN EINE ZEILE. Zehn Werte sind mehrzeilige
           Texte - Kundenmails, die Einladung, die Dienstadressen je
           Bundesland - und wurden in einem `<input>` bearbeitet, in dem man
           das Ende nicht sieht. */
        return (
          <textarea value={wert} disabled={!pflegen || laeuft} className={falsch}
                    rows={Math.min(24, Math.max(6, wert.split('\n').length + 1))}
                    style={{ width: '100%' }}
                    onChange={(e) => setWert(e.target.value)} />
        );

      default:
        return freiesFeld();
    }
  };

  /* DER ZWECK STEHT NUR DA, WENN ER ETWAS SAGT.
     Auf Marvins Bildschirmfoto stand unter „E-Mail-Adresse für Testmails"
     noch einmal „E-Mail-Adresse für Testmails" - der Titel und der Zweck
     waren derselbe Satz, weil die alte Vorgängerlösung nur EINE Beschriftung
     kannte. Drei Zeilen für eine Auskunft: genau das „grau in grau", das er
     beanstandet. Verglichen wird ohne Rücksicht auf Punkt und Grossschreibung,
     sonst rettet ein Schlusspunkt die Dopplung über die Prüfung. */
  const glatt = (s: unknown) =>
    String(s ?? '').toLowerCase().replace(/[.\s]+/g, ' ').trim();
  const zweck = glatt(x.zweck) && glatt(x.zweck) !== glatt(x.titel)
                ? x.zweck : null;

  /* EIN LANGTEXT BEKOMMT DIE GANZE BREITE.
     Marvin, 01.09.2026: „textfelder mit emailtext müssen vollständig lesbar
     sein, du musst die spaltengröße anpassen, die erste kann viel kleiner
     sein." Eine Kundenmail ist ein halbseitiger Brief; in einer 320 Pixel
     breiten Spalte liest man ihn nicht, man errät ihn. */
  const breit = art === 'langtext' && !x.geheim;

  return (
    <div className={'einstellung' + (breit ? ' breit' : '')
                    + (schmutzig ? ' geaendert' : '')}>
      <div className="einstellung-name">
        <div className="einstellung-titel">
          {/* DER ZUSTANDSPUNKT IST DIE ORDNUNG IN DER LISTE. Hundert Zeilen
              in derselben Graustufe sind eine Fläche; drei Punkte daneben
              sagen auf einen Blick, was gesetzt ist, was leer ist und was
              fehlt. */}
          <span className={'einstellung-punkt'
                           + (x.pflicht && !x.gesetzt ? ' fehlt'
                            : x.gesetzt ? ' gesetzt' : '')}
                aria-hidden="true" />
          {x.titel ?? x.schluessel}
          {x.pflicht && !x.gesetzt && <span className="merker fehler">fehlt</span>}
          {/* DER MERKER SAGT, WAS PASSIERT, UND NICHT, WELCHE GESTALT DER
              WERT HAT. Hier stand „Schalter" - und von den fünf Einstellungen
              mit Rückfrage sind nur drei Schalter: der zweite Faktor ist seit
              Migration 125 eine Gruppenliste, das n8n-Token ein Text. Eine
              Aufschrift, die etwas anderes sagt als der Weg dahinter tut, ist
              schlimmer als keine. */}
          {gefahr && (
            <span className="merker warnung"
                  title="Diese Einstellung braucht eine ausdrückliche Bestätigung.">
              Rückfrage
            </span>
          )}
        </div>
        {zweck && (
          <div className="klein leise" style={{ marginTop: 2 }}>{zweck}</div>
        )}
        {/* DER BEZEICHNER BLEIBT SICHTBAR, ABER KLEIN. Er wird gebraucht,
            sobald jemand sagt „stell betrieb.testmodus auf nein" - und er
            steht in jeder Fehlermeldung des Servers. */}
        <div className="einstellung-kennung">{x.schluessel}</div>
      </div>

      <div className="einstellung-feld">
        {feld()}
        <Fehlerkasten lage={fehler} />
        {gefaehrlichJetzt && gefahr && (
          <div className="hinweis fehler" style={{ marginTop: 8 }}>
            {gefahr.was}
            <label className="wahlzeile" style={{ marginTop: 6, cursor: 'pointer' }}>
              <input type="checkbox" checked={klar}
                     onChange={(e) => setKlar(e.target.checked)} />
              <span>Mir ist klar, was das bedeutet.</span>
            </label>
          </div>
        )}
        {/* DIE KNÖPFE ERSCHEINEN ERST, WENN WIRKLICH ETWAS ANDERS IST. Zwei
            Knöpfe an jeder von hundert Zeilen sind zweihundert Knöpfe. */}
        {pflegen && schmutzig && !sofort && (
          <div className="reihe" style={{ marginTop: 8, gap: 8 }}>
            <button className="knopf klein haupt"
                    disabled={laeuft || (gefaehrlichJetzt && !klar)}
                    onClick={() => schreibe(wert, klar)}>speichern</button>
            <button className="knopf klein" disabled={laeuft}
                    onClick={() => { setWert(original); setKlar(false);
                                     setFehler(null); }}>
              zurück
            </button>
          </div>
        )}
      </div>
    </div>
  );
}


/* HIER STAND DAS FENSTER „SCHLÜSSEL ANLEGEN".
 *
 * ORIGINALTEXT MARVIN (01.09.2026)
 *   „wir werden auch keine „Schlüssel" mehr anlegen, weil das system ganz
 *    anders arbeitet und aufgbaut ist. das habe ich nun schon mehrfach
 *    gesagt!"
 *
 * Der Weg dahinter ist mit derselben Version weg (`POST /konfiguration`), und
 * das ist der Punkt: ein Fenster zu entfernen und die Tür stehen zu lassen,
 * ist keine Entscheidung, sondern eine Verkleidung. Ab jetzt entsteht ein
 * Schlüssel in einer Migration oder gar nicht - erst damit lässt sich die
 * Regel „wer einen Schlüssel anlegt, schliesst ihn im selben Zug an"
 * nachrechnen (Prüfstand 061).
 */


/** Steht der Mailweg? Das laesst sich pruefen, ohne ein Papier zu opfern. */
function Probemail({ zu, testadresse }: any) {
  const [an, setAn] = useState(testadresse);
  const [laeuft, setLaeuft] = useState(false);
  const [antwort, setAntwort] = useState<string | null>(null);
  const [fehler, setFehler] = useState<Fehlerlage | null>(null);
  return (
    <Dialog titel="Probemail" laeuft={laeuft} zu={zu} breite={560}>
      <Fehlerkasten lage={fehler} />
      {antwort && <div className="hinweis">{antwort}</div>}
      <Text name="an" titel="An" wert={an} setze={setAn} breite="100%"
            fehler={fehler} hinweis="leer = die eingetragene Testadresse" />
      <p className="klein leise">
        Sie geht über den n8n-Webhook (N8N_MAIL_WEBHOOK_URL). Steht dort
        nichts, sagt die Antwort das.
      </p>
      <div className="reihe" style={{ marginTop: 10 }}>
        <button className="knopf haupt" disabled={laeuft}
                onClick={async () => {
                  setLaeuft(true); setFehler(null); setAntwort(null);
                  try { const e = await api.mailProbe(an || undefined);
                        setAntwort(e.klartext); }
                  catch (x) { setFehler(ausFehler(x)); }
                  finally { setLaeuft(false); }
                }}>
          {laeuft ? 'Einen Moment' : 'Senden'}
        </button>
      </div>
    </Dialog>
  );
}


/**
 * Eine Probenachricht an den Innendienstchat.
 *
 * ORIGINALTEXT MARVIN (27.08.2026): „Ist die Anbindung zu telegram
 * vorbereitet?"
 *
 * SIE WAR ES, UND SIE WAR TROTZDEM NICHT ANSCHLUSSFÄHIG: der Bot-Token wird
 * aus `TELEGRAM_BOT_TOKEN` gelesen, den keine Migration anlegte und den
 * „Schlüssel anlegen" wegen des Namensmusters (`bereich.name`, klein, mit
 * Punkt) auch nicht anlegen konnte. Der Schlüssel, den das Programm liest,
 * konnte auf keinem gebauten Weg entstehen.
 *
 * DIESES FENSTER SAGT, WAS PASSIERT IST, UND NICHT „ok". Drei Ausgänge
 * bedeuten drei verschiedene Dinge, und wer sie zu einem „erfolgreich"
 * zusammenzieht, erfährt den Unterschied erst im Ernstfall.
 */
/**
 * Tragen die Kartendienste? Mit einem ECHTEN Testbild je Dienst, für eine
 * Postleitzahl, die man eintippt: Limburg liegt in Hessen, Koblenz in
 * Rheinland-Pfalz, und ob der Eintrag für ein Land trägt, sieht man nur an
 * einer Adresse in diesem Land.
 *
 * ORIGINALTEXT MARVIN (02.09.2026)
 *   „1. kein Link für die Luftbilder von Hessen drin steht. Der war aber in
 *    der alten Plattform drin"
 */
function Kartendienste() {
  const [plz, setPlz] = useState('56070');
  const [laeuft, setLaeuft] = useState(false);
  const [a, setA] = useState<any>(null);
  const [fehler, setFehler] = useState<Fehlerlage | null>(null);

  async function los() {
    setLaeuft(true); setFehler(null); setA(null);
    try { setA(await api.geoPruefen(plz.trim())); }
    catch (f) { setFehler(ausFehler(f)); } finally { setLaeuft(false); }
  }
  async function neuLesen() {
    setLaeuft(true); setFehler(null);
    try { await api.geoNeuLesen(); await los(); }
    catch (f) { setFehler(ausFehler(f)); setLaeuft(false); }
  }

  const Zeile = ({ name, d }: { name: string; d: any }) => (
    <div className={'hinweis ' + (d?.ok ? '' : 'warnung')} style={{ marginTop: 6 }}>
      <b>{name}: {d?.ok ? 'trägt' : 'trägt NICHT'}</b>
      <div className="klein" style={{ fontWeight: 400, marginTop: 4 }}>
        {d?.eingetragen
          ? <>Eingetragen: <code>{d.eingetragen.url}</code>, Layer <code>{d.eingetragen.layer}</code></>
          : 'Für dieses Bundesland ist nichts eingetragen.'}
        {/* WER WIRKLICH GEANTWORTET HAT.
            ORIGINALTEXT MARVIN (03.09.2026): „Straßenkarte: trägt ·
            Eingetragen: sgx.geodatenzentrum.de, Ein Testbild von 2374 Bytes
            ist angekommen" - das Bild kam von einer ANDEREN Quelle. Seit .96
            werden mehrere der Reihe nach gefragt; steht hier nur der
            eingetragene Dienst, ändert der nächste eine Zeile, an der nichts
            hängt. */}
        {d?.geantwortet && d.geantwortet !== d?.eingetragen?.url && (
          <><br />Geantwortet hat: <code>{d.geantwortet}</code></>
        )}
        {d?.meldung && <><br />{d.meldung}</>}
        {/* WORAN ES LIEGT, wenn nur „fetch failed" dasteht.
            ORIGINALTEXT MARVIN (03.09.2026): „auch die karte ohen luftbild
            funktioniert nicht" - auf seinem Bildschirm standen alle vier
            Dienste auf „trägt NICHT", jeder mit demselben nichtssagenden
            Satz. Vier unabhängige Anbieter fallen nicht gleichzeitig aus;
            was ausfällt, ist der Weg dorthin. Diese Zeile sagt, ob der Name
            überhaupt auflöst und auf welche Adresse. */}
        {d?.woran && (
          <><br /><b style={{ fontWeight: 600 }}>Woran es liegt:</b> {d.woran}</>
        )}
        {typeof d?.gefunden === 'number' && d.ok && <><br />{d.gefunden} Flurstücke im Testausschnitt.</>}
        {d?.quelle && <><br />Quellenangabe: {d.quelle}</>}
        {!!d?.angeboteneLayer?.length && (
          <><br />Der Dienst bietet an: {d.angeboteneLayer.join(', ')}</>
        )}
      </div>
    </div>
  );

  return (
    <div style={{ marginTop: 14 }}>
      <div className="reihe" style={{ flexWrap: 'wrap' }}>
        <label className="feldpaar klein">
          PLZ einer Baustelle
          <input value={plz} onChange={(e) => setPlz(e.target.value)}
                 style={{ width: 90 }} inputMode="numeric" />
        </label>
        <button className="knopf haupt" onClick={los} disabled={laeuft}>
          {laeuft ? 'Einen Moment' : 'Dienste prüfen'}
        </button>
        <button className="knopf" onClick={neuLesen} disabled={laeuft}
                title="Die Einträge oben neu lesen, ohne den Server neu zu starten">
          Dienste neu lesen
        </button>
        <span className="klein leise">
          Holt je Dienst ein echtes Testbild für diese Postleitzahl. Limburg
          (65549) ist Hessen, Koblenz (56070) ist Rheinland-Pfalz.
        </span>
      </div>
      <Fehlerkasten lage={fehler} />
      {a && (
        <>
          <p className="klein leise" style={{ margin: '8px 0 0' }}>
            PLZ {a.plz}: Bundesland {a.land ?? 'nicht bestimmbar'}.
            {!!a.fehler?.length && <> Nicht lesbare Einträge: {a.fehler.join(' ')}</>}
          </p>
          {/* DER DIENST MIT SCHLÜSSEL STEHT OBEN.
              ORIGINALTEXT MARVIN (03.09.2026): „beim alten war auch keine
              kosten, wir haben den token eingebunden und das wars. wo ist das
              problem jetzt hier??" Der Schlüssel stand die ganze Zeit da und
              wurde von der Karte nie gefragt. Er steht deshalb an erster
              Stelle: er ist die Antwort auf seine Frage. */}
          {a.kartendienst && (
            <div className={'hinweis '
                  + (a.kartendienst.strasse?.ok ? '' : 'warnung')}
                 style={{ marginTop: 6 }}>
              <b>Kartendienst mit Schlüssel:{' '}
                {a.kartendienst.schluessel
                  ? (a.kartendienst.strasse?.ok ? 'trägt' : 'trägt NICHT')
                  : 'kein Schlüssel eingetragen'}</b>
              <div className="klein" style={{ fontWeight: 400, marginTop: 4 }}>
                Straßenkarte: {a.kartendienst.strasse?.meldung}
                <br />Luftbild (Satellit): {a.kartendienst.luftbild?.meldung}
                <br />Eingestellt: <code>karte.google_nutzen</code> ={' '}
                <b>{a.kartendienst.nutzung}</b>
                {a.kartendienst.adresse && (
                  <><br />Gefragt wird: <code>{a.kartendienst.adresse}</code></>
                )}
                <br /><span className="leise">{a.kartendienst.hinweis}</span>
              </div>
            </div>
          )}
          <Zeile name="Luftbild" d={a.luftbild} />
          {/* WIE FEIN DAS LUFTBILD WIRKLICH IST - gemessen, nicht behauptet.
           *
           * ORIGINALTEXT MARVIN (04.09.2026)
           *   „und ich bin der meinung das die geodaten noch schärfer sind
           *    von den bilder als das was du eingebaut hats. prüfe das gegen"
           *
           * Derselbe Ausschnitt wird zweimal geholt, einmal in der Auflösung
           * der Stufe und einmal in der doppelten. Was das doppelte Bild beim
           * Halbieren an Streuung VERLIERT, ist die Zeichnung, die zwischen
           * zwei Bildpunkten steckt; verliert es nichts, ist es hochgezogen.
           * Zum Vergleich: die Orthophotos von Rheinland-Pfalz haben 20 cm
           * Bodenauflösung, feiner veröffentlicht das Land nichts. */}
          {a.luftbild?.feinheit !== null
           && a.luftbild?.feinheit !== undefined && (
            <div className="klein leise" style={{ margin: '2px 0 8px 4px' }}>
              Auf Stufe 19 sind das <b>{a.luftbild.bodenCm} cm je
              Bildpunkt</b> (bei doppelter Punktdichte
              {' '}{a.luftbild.bodenCmDoppelt} cm).
              {a.luftbild.feinheitDoppelt !== null && (
                <> Das doppelt aufgelöste Bild behält beim Halbieren{' '}
                  {String(a.luftbild.feinheitDoppelt).replace('.', ',')} %
                  seiner Streuung
                  {a.luftbild.hochgezogen
                    ? ' - es trägt dort also kaum eigene Einzelheiten mehr, '
                      + 'die Quelle ist auf dieser Stufe ausgereizt.'
                    : ' - es trägt dort also eigene Einzelheiten.'}
                </>
              )}
            </div>
          )}
          <Zeile name="Grundstücksgrenzen" d={a.flurstuecke} />
          <Zeile name="Straßenkarte (wenn das Luftbild aus ist)" d={a.strassenkarte} />
          {/* Marvin, 02.09.2026: „es fehlt aber der straßenname und die
              hausnummern." Sie liegt über dem Luftbild, und ein Dienst, der
              schweigt, ist von einer Gegend ohne Straßennamen nicht zu
              unterscheiden - deshalb steht er hier mit seinem Testbild. */}
          <Zeile name="Beschriftung (Straßennamen über dem Luftbild)"
                 d={a.beschriftung} />
        </>
      )}
    </div>
  );
}

function Telegramprobe({ zu }: { zu: () => void }) {
  const [laeuft, setLaeuft] = useState(false);
  const [a, setA] = useState<any>(null);
  const [fehler, setFehler] = useState<Fehlerlage | null>(null);

  async function los() {
    setLaeuft(true); setFehler(null); setA(null);
    try { setA(await api.telegramProbe()); }
    catch (f) { setFehler(ausFehler(f)); } finally { setLaeuft(false); }
  }

  return (
    <Dialog titel="Telegram prüfen" laeuft={laeuft} zu={zu} breite={560}>
      <Fehlerkasten lage={fehler} />
      <p className="klein leise" style={{ marginTop: 0 }}>
        Schickt eine Probenachricht an den Innendienstchat
        („AUTOMATIK_CHAT"). <b>Der Testmodus bleibt darüber:</b> solange
        „sicherheit.telegram_testmodus" auf „ja" steht, geht nichts hinaus -
        die Probe sagt dann trotzdem, ob Token und Chat stimmen würden.
      </p>
      {a && (
        <div className={'hinweis ' + (a.gesendet ? '' : 'warnung')}>
          <b>{a.was}</b>
          <div className="klein" style={{ fontWeight: 400, marginTop: 4 }}>
            Empfänger: {a.empfaenger}. Bot-Token{' '}
            {a.hat_token ? 'hinterlegt' : 'FEHLT'}, Testmodus{' '}
            {a.testmodus ? 'an' : 'aus'}.
            {' '}Jeder Versuch steht im Protokoll, auch der unterdrückte.
          </div>
        </div>
      )}
      <div className="reihe" style={{ marginTop: 12 }}>
        <div className="wachsen" />
        <button className="knopf" onClick={zu} disabled={laeuft}>
          Schließen
        </button>
        <button className="knopf haupt" onClick={los} disabled={laeuft}>
          {laeuft ? 'Einen Moment' : 'Probenachricht senden'}
        </button>
      </div>
    </Dialog>
  );
}


/**
 * Das Firmenzeichen austauschen.
 *
 * ORIGINALTEXT MARVIN (27.08.2026)
 *   „wo lade ich mein logo hoch und erstellst du mir ein favicon aus dem
 *    symbol bitte."
 *
 * DAS FAVICON KOMMT AUS DEM BÜNDEL, das Zeichen der Seitenleiste von hier.
 * Das ist kein Widerspruch, sondern zwei verschiedene Dinge: das Symbol im
 * Browserreiter wird beim Bauen mitgeliefert (der Browser holt es, bevor
 * irgendetwas angemeldet ist und lange bevor die Anwendung läuft), das
 * Zeichen in der Oberfläche liegt in der Ablage und lässt sich hier
 * austauschen, ohne dass jemand etwas ausliefern muss.
 *
 * KEINE SVG. Eine SVG-Datei darf `<script>` enthalten, und der Browser führt
 * es aus, wenn man sie direkt öffnet - auf unserer Adresse. Das mitgelieferte
 * Zeichen darf eine sein, denn die hat jemand geschrieben; eine hochgeladene
 * wäre eine offene Tür.
 */
function Firmenzeichen({ zu }: { zu: () => void }) {
  const [laeuft, setLaeuft] = useState(false);
  const [fertig, setFertig] = useState<string | null>(null);
  const [fehler, setFehler] = useState<Fehlerlage | null>(null);
  // Damit das Bild nach dem Hochladen wirklich neu geholt wird und nicht aus
  // dem Zwischenspeicher kommt.
  const [stand, setStand] = useState(0);

  async function hoch(datei: File) {
    setLaeuft(true); setFehler(null); setFertig(null);
    try {
      const a = await api.markeLogoHochladen(datei);
      setFertig(a.meldung ?? 'Das Zeichen ist hinterlegt.');
      setStand(stand + 1);
    } catch (f) { setFehler(ausFehler(f)); } finally { setLaeuft(false); }
  }

  return (
    <Dialog titel="Firmenzeichen" laeuft={laeuft} zu={zu} breite={560}>
      <Fehlerkasten lage={fehler} />
      <p className="klein leise" style={{ marginTop: 0 }}>
        Das Zeichen steht in der Seitenleiste und auf der Anmeldeseite.
        <b> PNG, JPG oder WEBP</b>, quadratisch, höchstens 2 MB - es wird in
        einem Feld von 40 Pixeln gezeigt. Eine SVG-Datei nimmt dieser Weg
        NICHT an: sie darf Programmcode enthalten, den der Browser ausführt.
      </p>

      <div className="reihe" style={{ alignItems: 'center', gap: 12,
                                      marginBottom: 10 }}>
        <img src={'/api/marke/logo?v=' + stand} alt="" width={48} height={48}
             style={{ objectFit: 'contain' }}
             onError={(e) => {
               (e.currentTarget as HTMLImageElement).src = '/logo.png';
             }} />
        <span className="klein leise">So sieht es aus.</span>
      </div>

      <Abwurf titel="Das Zeichen hierher" klein laeuft={laeuft}
              laufText="Wird abgelegt" accept=".png,.jpg,.jpeg,.webp"
              hinweis="Ziehen oder tippen."
              nimm={(x) => { if (x[0]) hoch(x[0].datei); }} />

      {fertig && (
        <div className="hinweis klein" style={{ marginTop: 10 }}>{fertig}</div>
      )}

      <div className="reihe" style={{ marginTop: 12 }}>
        {/* ZURÜCK ZUM GELIEFERTEN ZEICHEN ist kein Löschen der Datei: die
            liegt weiter in der Ablage. Es ist nur der Verweis darauf. */}
        <button className="knopf" disabled={laeuft}
                onClick={async () => {
                  setLaeuft(true); setFehler(null);
                  try {
                    await api.markeLogoWeg();
                    setFertig('Es gilt wieder das mitgelieferte Zeichen.');
                    setStand(stand + 1);
                  } catch (f) { setFehler(ausFehler(f)); }
                  finally { setLaeuft(false); }
                }}>
          Zurück zum gelieferten Zeichen
        </button>
        <div className="wachsen" />
        <button className="knopf haupt" onClick={zu} disabled={laeuft}>
          Schließen
        </button>
      </div>
    </Dialog>
  );
}


/* „vor 4 Min.", „heute 10:24", „gestern 17:09", sonst Datum und Uhrzeit (.247).
   Deutsche Zeit, wie überall auf der Plattform. */
function wannAktiv(w: string): string {
  const t = new Date(w).getTime();
  if (!Number.isFinite(t)) return '';
  const min = Math.floor((Date.now() - t) / 60_000);
  if (min < 1) return 'gerade eben';
  if (min < 60) return `vor ${min} Min.`;
  const tag = (d: Date) => d.toLocaleDateString('de-DE', { timeZone: 'Europe/Berlin' });
  const uhr = new Date(t).toLocaleTimeString('de-DE', { timeZone: 'Europe/Berlin', hour: '2-digit', minute: '2-digit' });
  if (tag(new Date(t)) === tag(new Date())) return `heute ${uhr}`;
  if (tag(new Date(t)) === tag(new Date(Date.now() - 86_400_000))) return `gestern ${uhr}`;
  return zeitpunkt(w);
}


/** Meldet sich mit den gespeicherten Daten in der E-Vergabe an und gleich wieder ab (.569). */
function EvergabePruefen() {
  const [lage, setLage] = useState<{ ok: boolean; text: string } | null>(null);
  const [laeuft, setLaeuft] = useState(false);
  return (
    <div className="reihe" style={{ gap: 10, alignItems: 'center', marginTop: 10 }}>
      <button type="button" className="knopf" disabled={laeuft}
              onClick={async () => { setLaeuft(true); setLage(null); try { setLage(await api.evergabePruefen()); } catch (f: any) { setLage({ ok: false, text: String(f?.message ?? f) }); } finally { setLaeuft(false); } }}>
        {laeuft ? 'Prüfe …' : 'Zugang prüfen'}
      </button>
      {lage && <span className={'merker ' + (lage.ok ? 'gruen' : 'fehler')}>{lage.text}</span>}
      {!lage && <span className="klein leise">Meldet sich mit Benutzer und Passwort an und sofort wieder ab. Im Portal wird nichts geändert.</span>}
    </div>
  );
}
